<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, provide, onErrorCaptured } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useContactStore } from '../stores/contacts.store';
import { contactsApi } from '../api/contactsApi';
import { useDialog } from '@/shared/composables/useDialog';
import BasicIndividualSection from '../components/BasicIndividualSection.vue';
import SyncFieldIndicator from '@features/Entity_my/components/SyncFieldIndicator.vue';
import { useSyncFieldChanges } from '../composables/useSyncFieldChanges';
import { entityMyApi } from '@features/Entity_my/api/entityApi';
import BasicCorporateSection from '../components/BasicCorporateSection.vue';
import ContactAddressForm from '../components/ContactAddressForm.vue';
import NoSyncContactAddressForm from '../components/NoSyncContactAddressForm.vue';
import ContactInfoForm from '../components/ContactInfoForm.vue';
import CorporateLegalInfoSection from '../components/CorporateLegalInfoSection.vue';
import CompanyRelationsTable from '../components/CompanyRelationsTable.vue';
import KYCSection from '../components/KYCSection.vue';
import AddressCompareDevDialog from '../components/AddressCompareDevDialog.vue';
import type { UpdateContactData, SpcAddressConsolidationGroupTab } from '../types/contact.types';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { usePermissionGuard } from '@/shared/composables/usePermissionGuard';
import { isLocal, isDevelopment } from '@/shared/config/env';
import { extractAddressArrayFromProfileJsonColumn } from '@features/Contact/utils/profileServiceBusinessColumn';
import { countDistinctSpcAddressDisplayLines } from '@features/Contact/utils/spcAddressCompareCompact';
import { contactDetailMyResidentialEditingKey } from '../contactDetailInjectionKeys';
import { isMyPassportIdType } from '../utils/myPassportIdType';
import type { MyCorporateBranchAddressLists } from '../utils/persistMyCorporateBranchAddresses';
import type { EntityOfficerContactOverlay, EntityBeneficialOwnerContactOverlay } from '@features/Entity_my/types/entity.types';
import {
    fetchEntityRegisterContactLikeForDetail,
    mergeBaseContactWithRegisterAddressLike,
    resolveEntityRegisterDetailAddressContext,
} from '@features/Entity_my/utils/entityRegisterContactAddressDetail';
import { hydrateMyContactDetailForms, type MyContactDetailFormState } from '@/features/Contact/utils/hydrateMyContactDetailForms';
import { MY_REGISTER_POSITION_CD } from '@/features/register/MY/constants/myRegisterPositionCd';
import {
    applyRegisterOfficerAddressConstraints,
    sliceOfficerAddressSaveList,
    stripRegisterBusinessAddress,
} from '@/features/register/MY/utils/registerOfficerAddressConstraints';
import { saveMyRegisterContactProfileAndAddresses } from '@/features/register/MY/utils/saveMyRegisterRoleContactAddresses';
import type { EntityDirectorAddressSaveRefs } from '@/features/register/MY/utils/saveMyRegisterEntityDirector';

// Props 정의
interface Props {
    contactId?: string;
    /** Entity_my 등 — MY Corporate branch 주소 조회 시 ENTITY_CD 필터 */
    entityCd?: string;
    isModal?: boolean;
    onSelect?: (contact: any) => void;
    rememberedPosition?: { positionId?: string; positionName?: string } | null;
    /** Entity Officer 모달 — L_OFFICER 직책별 표시 필드 */
    entityOfficerOverlay?: EntityOfficerContactOverlay | null;
    /** Entity Beneficial Owner 모달 — L_BENEFICIALOWNER.OCCUPATION */
    entityBeneficialOwnerOverlay?: EntityBeneficialOwnerContactOverlay | null;
    /** Entity 상세 — L_CONTACT_BRANCH_MAP 역할별 주소 (B30 position code) */
    registerRolePositionCd?: string;
    /** Contact 메뉴(/Contact)에서 연 상세 — Entity·Register 모달과 구분 */
    contactListMenuMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    contactId: '',
    entityCd: '',
    isModal: false,
    onSelect: undefined,
    entityOfficerOverlay: null,
    entityBeneficialOwnerOverlay: null,
    registerRolePositionCd: '',
    contactListMenuMode: false,
});

// Emits 정의
const emit = defineEmits<{
    'back': [];
    'updated': [contact: any];
    'select': [contact: any];
    'register': [data: { contactId: string; positionId?: string; positionName?: string }];
}>();

// 레지스터 등록 체크박스 상태
const registerActivityHistory = ref(false);

// 원본 데이터 저장 (수정 감지용)
const originalContactData = ref<any>(null);
const entityOfficerForm = ref<EntityOfficerContactOverlay | null>(null);
const originalEntityOfficerForm = ref<EntityOfficerContactOverlay | null>(null);
const entityBeneficialOwnerForm = ref<EntityBeneficialOwnerContactOverlay | null>(null);
const originalEntityBeneficialOwnerForm = ref<EntityBeneficialOwnerContactOverlay | null>(null);

watch(
    () => props.entityOfficerOverlay,
    (overlay) => {
        entityOfficerForm.value = overlay ? { ...overlay } : null;
        originalEntityOfficerForm.value = overlay ? { ...overlay } : null;
    },
    { immediate: true },
);

watch(
    () => props.entityBeneficialOwnerOverlay,
    (overlay) => {
        entityBeneficialOwnerForm.value = overlay ? { ...overlay } : null;
        originalEntityBeneficialOwnerForm.value = overlay ? { ...overlay } : null;
    },
    { immediate: true },
);

const hasEntityOfficerChanges = computed(() => {
    const current = entityOfficerForm.value;
    const original = originalEntityOfficerForm.value;
    if (!current || !original) return false;
    return (
        String(current.occupation ?? '').trim() !== String(original.occupation ?? '').trim()
        || String(current.secretaryLicenseNo ?? '').trim() !== String(original.secretaryLicenseNo ?? '').trim()
        || String(current.secretaryCertificateNo ?? '').trim() !== String(original.secretaryCertificateNo ?? '').trim()
        || String(current.firmNo ?? '').trim() !== String(original.firmNo ?? '').trim()
    );
});

const hasEntityBeneficialOwnerChanges = computed(() => {
    const current = entityBeneficialOwnerForm.value;
    const original = originalEntityBeneficialOwnerForm.value;
    if (!current || !original) return false;
    return String(current.occupation ?? '').trim() !== String(original.occupation ?? '').trim();
});

// 수정 감지 computed
const hasContactChanges = computed(() => {
  if (!contactStore.currentContact || !originalContactData.value) {
    return hasEntityOfficerChanges.value || hasEntityBeneficialOwnerChanges.value;
  }
  
  const current = contactStore.currentContact;
  const original = originalContactData.value;
  
  // 주요 필드 변경 체크
  const fieldsToCheck = [
    'FIRST_NAME', 'MIDDLE_NAME', 'SURNAME', 'CONTACT_NAME',
    'ID_TYPE_CD', 'CONTACT_REG_NO', 'NATIONALITY_CD', 'DATE_OF_BIRTH', 'PASSPORT_NO_EXPIRY_DATE',
    'COUNTRY_CD', 'AREA_CODE', 'MOBILE_NO', 'CONTACT_EMAIL',
    'POSTAL_CODE', 'BLOCK_NO', 'STREET_NAME', 'BUILDING_NAME',
    'ADDR_LEVEL', 'ADDR_UNIT', 'FOREIGN_ADDRESS_LINE_1', 'FOREIGN_ADDRESS_LINE_2',
    'ADDRESS_TYPE_CD', 'NOTE',
    'PROFILE_GENDER', 'PROFILE_RACE', 'PROFILE_OCCUPATION', 'PROFILE_SECRETARY_LICENSE_NO', 'PROFILE_SECRETARY_CERTIFICATE_NO',
    'PROFILE_OTHER_DIRECTORSHIP', 'PROFILE_FIRM_NO', 'PROFILE_ISSUING_COUNTRY', 'PROFILE_PLACE_OF_BIRTH',
    'ALT_POSTAL_CODE', 'ALT_BLOCK_NO', 'ALT_STREET_NAME', 'ALT_BUILDING_NAME',
    'ALT_ADDR_LEVEL', 'ALT_ADDR_UNIT', 'ALT_FOREIGN_ADDRESS_LINE_1', 'ALT_FOREIGN_ADDRESS_LINE_2'
  ];
  
  for (const field of fieldsToCheck) {
    const currentValue = (current as any)[field] || '';
    const originalValue = (original as any)[field] || '';
    if (String(currentValue).trim() !== String(originalValue).trim()) {
      return true;
    }
  }
  
  // 이메일 배열 비교
  const currentEmails = (current as any).CONTACT_EMAILS || [];
  const originalEmails = (original as any).CONTACT_EMAILS || [];
  if (JSON.stringify(currentEmails) !== JSON.stringify(originalEmails)) {
    return true;
  }
  
  return hasEntityOfficerChanges.value || hasEntityBeneficialOwnerChanges.value;
});

const route = useRoute();
const router = useRouter();
const contactStore = useContactStore();
const sessionStore = useSessionStore();

const myResidentialAddressEditing = ref(false);
provide(contactDetailMyResidentialEditingKey, myResidentialAddressEditing);
const { alert, confirm } = useDialog();
const { createGuardedAction } = usePermissionGuard();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});

/** Contact 기본 주소 표시: isPrimaryAddress 가 1 인 항목만 (Relations 테이블 제외) */
function isPrimaryAddressDisplayFlag(value: unknown): boolean {
    return value === 1 || value === '1' || value === true;
}

function filterContactJsonPrimaryOnly(list: unknown): any[] {
    if (!Array.isArray(list)) return [];
    return list.filter((item: any) =>
        isPrimaryAddressDisplayFlag(item?.isPrimaryAddress ?? item?.is_primary_address)
    );
}

/** MY: SERVICE_ADDR_JSON_DATA / BUSINESS_ADDR_JSON_DATA → 표시·altLocalAddress용 목록 (primary 없으면 전체) */
function myProfileAddrListPrimaryOrAll(raw: unknown, key: 'service_address' | 'business_address'): any[] {
    const arr = extractAddressArrayFromProfileJsonColumn(raw, key);
    if (arr.length === 0) return [];
    const prim = arr.filter((item: Record<string, unknown>) =>
        isPrimaryAddressDisplayFlag(item?.isPrimaryAddress ?? item?.is_primary_address)
    );
    const list = prim.length > 0 ? prim : arr;
    return list.map((row: Record<string, unknown>) => ({ ...row }));
}

/** Residential 표시용: 배열이면 primary → 의미 있는 첫 행 → [0], 단일 객체는 primary 또는 주소 필드가 있으면 그대로 */
function pickPrimaryResidentialForDisplay(addr: any): any | null {
    if (addr == null) return null;
    if (Array.isArray(addr)) {
        if (addr.length === 0) return null;
        const primary = addr.find((item: any) =>
            isPrimaryAddressDisplayFlag(item?.isPrimaryAddress ?? item?.is_primary_address)
        );
        if (primary) return primary;
        const meaningful = addr.find((item: any) => isAddressMeaningful(item));
        if (meaningful) return meaningful;
        const first = addr[0];
        return first && typeof first === 'object' ? first : null;
    }
    if (typeof addr === 'object') {
        const flag = addr?.isPrimaryAddress ?? addr?.is_primary_address;
        if (isPrimaryAddressDisplayFlag(flag)) return addr;
        if (isAddressMeaningful(addr)) return addr;
        return null;
    }
    return null;
}

/** L_GLOBAL_CONTACT_PROFILE.RESIDENTIAL_ADDR_JSON_DATA 컬럼(또는 API 동일 필드)에서 primary 또는 단일 주소 객체 추출 */
function getResidentialPrimaryFromResidentialAddrJsonDataColumn(contact: any): any | null {
    const raw = (contact as any)?.RESIDENTIAL_ADDR_JSON_DATA ?? (contact as any)?.residential_addr_json_data;
    if (raw == null || raw === '') return null;
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== 'object') return null;
        if (parsed.residential_address != null) {
            const inner = pickPrimaryResidentialForDisplay(parsed.residential_address);
            if (inner) return inner;
        }
        const fromRoot = pickPrimaryResidentialForDisplay(parsed);
        if (fromRoot) return fromRoot;
        const hasLine =
            String(parsed.addressLine1 ?? parsed.street01 ?? parsed.address1 ?? '').trim() !== '' ||
            String(parsed.addressLine2 ?? parsed.street02 ?? '').trim() !== '' ||
            String(parsed.addressLine3 ?? parsed.street03 ?? '').trim() !== '' ||
            String(parsed.country ?? '').trim() !== '' ||
            String(parsed.postcode ?? parsed.post_code ?? '').trim() !== '';
        if (hasLine) return parsed;
    } catch {
        return null;
    }
    return null;
}

/**
 * MY Residential 표시·폼 바인딩.
 * Corporate + USE_BRANCH_ADDRESSES: API residential_address 배열.
 * Contact List 개인: L_CONTACT_ADDRESS (residential_address / registeredAddress) 우선, Profile 폴백.
 */
function pickResidentialForMyAddressForm(contact: any): any | null {
    if (contact?.USE_BRANCH_ADDRESSES) {
        const ra = (contact as any).residential_address;
        if (Array.isArray(ra) && ra.length > 0) {
            return pickPrimaryResidentialForDisplay(ra) || ra[0];
        }
        return null;
    }
    const fromArray = pickPrimaryResidentialForDisplay((contact as any)?.residential_address);
    if (fromArray) return fromArray;
    const regFromApi = (contact as any)?.registeredAddress;
    if (regFromApi && typeof regFromApi === 'object' && isAddressMeaningful(regFromApi)) {
        return { ...regFromApi };
    }
    return getResidentialPrimaryFromResidentialAddrJsonDataColumn(contact);
}

// contactId는 props에서 받거나 route에서 가져오기
const contactId = computed(() => props.contactId || (route.params.id as string));
const deleteDialog = ref(false);

// 싱크 변경 인디케이터 — Entity(officer) 컨텍스트(entityCd 있음)에서만 동작.
//   Contact 메뉴(entityCd 없음)에서는 빈 맵 → 인디케이터 미표시.
const syncEntityCd = computed(() => String(props.entityCd || '').trim());
const syncMatch = computed(() => {
    const c = (contactStore.currentContact || {}) as any;
    return {
        contactCd: String(contactId.value || '').trim(),
        identificationNumber: String(c.CONTACT_REG_NO || c.contactRegNo || c.identificationNo || '').trim(),
        profileId: String(c.PROFILE_ID || c.profileId || '').trim(),
        personName: String(c.CONTACT_NAME || c.contactName || '').trim(),
    };
});
const { syncFieldChanges, fieldChange: syncFieldChange, reloadSyncFieldChanges } = useSyncFieldChanges(syncEntityCd, syncMatch);
const isSubmitting = ref(false);

const loadContact = async () => {
    try {
        const cid = contactId.value;
        if (!cid) {
            console.warn('[ContactDetailPage] loadContact: skip — no contactId');
            return;
        }

        console.log('[ContactDetailPage] loadContact: start', {
            contactCd: cid,
            entityCd: String(props.entityCd || '').trim() || null,
        });

        addressData.value = null;
        contactInfoData.value = null;
        branchResidentialDraft.value = [];

        const entityCd = String(props.entityCd || '').trim();
        await contactStore.fetchContactById(
            cid,
            entityCd ? { entityCd } : undefined,
        );

        const c0 = contactStore.currentContact as any;
        if (!c0) {
            console.warn('[ContactDetailPage] loadContact: no currentContact after fetch');
            return;
        }

        let c = c0;
        let registerCtx = resolveEntityRegisterDetailAddressContext({
            entityCd,
            contactCd: cid,
            registerRolePositionCd: props.registerRolePositionCd,
            entityOfficerOverlay: props.entityOfficerOverlay,
            entityBeneficialOwnerOverlay: props.entityBeneficialOwnerOverlay,
        });
        registerDetailHydratedState.value = null;
        if (registerCtx) {
            try {
                const categoryCd = String(c.CATEGORY_CD || c.categoryCd || 'B09002').trim();
                const contactLike = await fetchEntityRegisterContactLikeForDetail(registerCtx, categoryCd);
                c = mergeBaseContactWithRegisterAddressLike(c, contactLike);
                if (contactLike.branch_address_show_pool) {
                    c.branch_address_show_pool = contactLike.branch_address_show_pool;
                }
                contactStore.currentContact = c as any;

                let hydrated = hydrateMyContactDetailForms(c as Record<string, unknown>, entityCd);
                if (props.entityOfficerOverlay) {
                    hydrated = applyRegisterOfficerAddressConstraints(hydrated);
                }
                if (registerCtx.positionCd !== MY_REGISTER_POSITION_CD.BENEFICIAL_OWNER) {
                    hydrated = stripRegisterBusinessAddress(hydrated);
                }
                registerDetailHydratedState.value = hydrated;
                addressData.value = hydrated.addressData as any;
                contactInfoData.value = hydrated.contactInfoData as any;
            } catch (registerAddrErr) {
                console.warn('[ContactDetailPage] register role address load failed', registerAddrErr);
                registerCtx = null;
                hydrateDetailFromContact(c);
            }
        } else {
            hydrateDetailFromContact(c);
        }

        // Service/Business: ContactInfoForm 언마운트 시 addressEditState 초기화와 동일 —
        // Residential은 provide 상태가 유지되므로 서버 reload 후 요약(Edit) 모드로 복귀
        myResidentialAddressEditing.value = false;

        const info = contactInfoData.value as any;
        const branchResList = branchResidentialAddressList.value;
        console.log('[ContactDetailPage] detail ready', {
            contactCd: c.CONTACT_CD,
            category: c.CATEGORY_CD,
            isCorporate: contactStore.isCorporate,
            isIndividual: contactStore.isIndividual,
            useBranch: c.USE_BRANCH_ADDRESSES,
            useBranchOnDetail: useBranchAddressesOnDetail.value,
            residentialCount: Array.isArray(c.residential_address) ? c.residential_address.length : 0,
            branchResidentialListCount: branchResList.length,
            showRegisteredAddressForm: showRegisteredAddressForm.value,
            isPostalCdSyncEnabled: isPostalCdSyncEnabled.value,
            serviceCount: Array.isArray(c.service_address) ? c.service_address.length : 0,
            businessCount: Array.isArray(c.business_address) ? c.business_address.length : 0,
            hasAddressData: !!addressData.value,
            hasContactInfoData: !!contactInfoData.value,
            infoResidentialCount: Array.isArray(info?.residential_address) ? info.residential_address.length : 0,
        });

        if (syncEntityCd.value) {
            await reloadSyncFieldChanges();
        }

        // 원본 데이터 저장 (수정 감지용)
        if (contactStore.currentContact) {
            originalContactData.value = JSON.parse(JSON.stringify(contactStore.currentContact));
            // 체크박스 초기화
            registerActivityHistory.value = false;
        }
    } catch (error: any) {
        console.error('Failed to load contact:', error);
        await alert({
            title: t('contact.Error') || 'Error',
            message: error?.message || t('contact.Failed to load contact') || 'Contact 정보를 불러오는데 실패했습니다.',
            type: 'error'
        });
    }
};

const handleSave = async () => {
    if (isSubmitting.value) return;
    if (!contactStore.currentContact) {
        await alert({
            title: t('contact.Error') || 'Error',
            message: t('contact.Contact data is not available') || 'Contact data is not available',
            type: 'error'
        });
        return;
    }

    // 확인 다이얼로그 표시
    const confirmed = await confirm({
        title: t('contact.Confirm') || 'Confirm',
        message: t('contact.Do you want to save the changes?') || 'Do you want to save the changes?',
        type: 'question'
    });

    if (!confirmed) {
        return;
    }

    isSubmitting.value = true;
    try {
        const contact = contactStore.currentContact;
        
        // UpdateContactData 형식으로 변환
        const updateData: UpdateContactData = {};
        
        // Individual 필드
        const sessionJurisdiction = sessionStore.user?.customer?.jurisdiction || '';
        const isMyClient =
            sessionJurisdiction.toUpperCase() === 'MY' ||
            sessionJurisdiction.toUpperCase() === 'MALAYSIA';

        if (contactStore.isIndividual) {
            if (contact.ID_TYPE_CD) updateData.identificationType = contact.ID_TYPE_CD;
            if (contact.CONTACT_REG_NO) updateData.identificationNo = contact.CONTACT_REG_NO;
            if (contact.NATIONALITY_CD) updateData.nationality = contact.NATIONALITY_CD;
            // DATE_OF_BIRTH는 빈 문자열이어도 전송 (백엔드에서 null로 처리)
            if (contact.DATE_OF_BIRTH !== undefined) {
                updateData.dateOfBirth = contact.DATE_OF_BIRTH || undefined;
            }
            const isMyPassport = isMyClient && isMyPassportIdType(contact as Record<string, unknown>);
            if (isMyPassport) {
                if (contact.PASSPORT_NO_EXPIRY_DATE !== undefined) {
                    updateData.passportNoExpiryDate = contact.PASSPORT_NO_EXPIRY_DATE || undefined;
                }
            } else if (isMyClient) {
                updateData.passportNoExpiryDate = undefined;
            } else if (contact.PASSPORT_NO_EXPIRY_DATE !== undefined) {
                updateData.passportNoExpiryDate = contact.PASSPORT_NO_EXPIRY_DATE || undefined;
            }
            if (!isMyClient) {
                if (contact.FIRST_NAME) updateData.firstName = contact.FIRST_NAME;
                if (contact.MIDDLE_NAME !== undefined) updateData.middleName = contact.MIDDLE_NAME;
                if (contact.SURNAME) updateData.surname = contact.SURNAME;
            }
            if (contact.PROFILE_GENDER !== undefined) updateData.profileGender = contact.PROFILE_GENDER || '';
            if (contact.PROFILE_RACE !== undefined) updateData.profileRace = contact.PROFILE_RACE || '';
            if (contact.PROFILE_OCCUPATION !== undefined) updateData.profileOccupation = contact.PROFILE_OCCUPATION || '';
            if (contact.PROFILE_SECRETARY_LICENSE_NO !== undefined) updateData.profileSecretaryLicenseNo = contact.PROFILE_SECRETARY_LICENSE_NO || '';
            if (contact.PROFILE_SECRETARY_CERTIFICATE_NO !== undefined) updateData.profileSecretaryCertificateNo = contact.PROFILE_SECRETARY_CERTIFICATE_NO || '';
            if (contact.PROFILE_OTHER_DIRECTORSHIP !== undefined) updateData.profileOtherDirectorship = contact.PROFILE_OTHER_DIRECTORSHIP || '';
            if (contact.PROFILE_FIRM_NO !== undefined) updateData.profileFirmNo = contact.PROFILE_FIRM_NO || '';
            if (isMyPassport) {
                if (contact.PROFILE_ISSUING_COUNTRY !== undefined) {
                    updateData.profileIssuingCountry = contact.PROFILE_ISSUING_COUNTRY || '';
                }
            } else if (isMyClient) {
                updateData.profileIssuingCountry = '';
            }
            if (contact.PROFILE_PLACE_OF_BIRTH !== undefined) updateData.profilePlaceOfBirth = contact.PROFILE_PLACE_OF_BIRTH || '';
        }
        
        // Corporate 필드
        if (contactStore.isCorporate) {
            if (contact.CONTACT_NAME) updateData.entityName = contact.CONTACT_NAME;
            if (contact.CONTACT_REG_NO) updateData.identificationNo = contact.CONTACT_REG_NO;
            if ((contact as any).ID_TYPE !== undefined) updateData.idTypeJson = String((contact as any).ID_TYPE ?? '');
            if (contact.JURISDICTION) updateData.jurisdiction = contact.JURISDICTION;
            if (contact.DATE_OF_INCORPORATION) updateData.incorporationDate = contact.DATE_OF_INCORPORATION;
            if (contact.REPRESENTATIVE_CD !== undefined && contact.REPRESENTATIVE_CD !== null) {
                updateData.representativeName = contact.REPRESENTATIVE_CD;
            }
            if (contact.LEGAL_FORM !== undefined) updateData.legalForm = contact.LEGAL_FORM || '';
            if (contact.INCORPORATION_STATUTE !== undefined) updateData.incorporationStatute = contact.INCORPORATION_STATUTE || '';
            if (contact.CORPORATE_ENTITY_REGISTER !== undefined) updateData.corporateEntityRegister = contact.CORPORATE_ENTITY_REGISTER || '';
            // Public Accounting Entities (AUDITTING 컬럼)
            // BasicCorporateSection에서 업데이트된 값이 있는 경우에만 포함
            // contact.AUDITOR_YN 또는 basicData에서 가져오기
            const auditorValue = contact.AUDITOR_YN !== undefined ? contact.AUDITOR_YN : 
                                (contact as any).publicAccountingEntities;
            if (auditorValue !== undefined && auditorValue !== null) {
                updateData.publicAccountingEntities = (auditorValue === 'Yes' || auditorValue === true || auditorValue === 1 || auditorValue === '1') ? 'Yes' : 'No';
            }
            if (contact.PROFILE_FIRM_NO !== undefined) {
                updateData.profileFirmNo = contact.PROFILE_FIRM_NO || '';
            }
        }
        
        const registerAddrCtx = entityRegisterAddressContext.value;

        if (contact && !registerAddrCtx) {
            const addressType = contact.ADDRESS_TYPE_CD || 'B07001';
            
            // addressData.value.localAddress를 우선 사용 (최신 값)
            let localAddress: any = null;
            if (addressData.value?.localAddress) {
                localAddress = { ...addressData.value.localAddress };
                console.log('✅ [handleSave] addressData.value.localAddress 사용:', localAddress);
            } else {
                // 기존 방식: contact 객체에서 읽기
                localAddress = {
                    postalCode: contact.POSTAL_CODE || '',
                    blockHouseNo: contact.BLOCK_NO || '',
                    streetName: contact.STREET_NAME || '',
                    buildingEstateName: contact.BUILDING_NAME || '',
                    level: contact.ADDR_LEVEL || '',
                    unit: contact.ADDR_UNIT || '',
                };
                console.log('⚠️ [handleSave] contact 객체에서 localAddress 읽기:', localAddress);
            }
            
            // foreignAddress는 addressData에서 우선 사용
            let foreignAddress: any = null;
            if (addressData.value?.foreignAddress) {
                foreignAddress = { ...addressData.value.foreignAddress };
            } else {
                foreignAddress = {
                    addressLine1: contact.FOREIGN_ADDRESS_LINE_1 || '',
                    addressLine2: contact.FOREIGN_ADDRESS_LINE_2 || '',
                };
            }

            updateData.addressTypeCd = addressType;

            if (addressType === 'B07002') {
                // Service/Foreign Address
                updateData.foreignAddressLine1 = foreignAddress.addressLine1;
                updateData.foreignAddressLine2 = foreignAddress.addressLine2;
                updateData.postalCode = '';
                updateData.blockNo = '';
                updateData.streetName = '';
                updateData.buildingName = '';
                updateData.addrLevel = '';
                updateData.addrUnit = '';
            } else {
                // Residential/Local Address
                updateData.postalCode = localAddress.postalCode || '';
                updateData.blockNo = localAddress.blockHouseNo || '';
                updateData.streetName = localAddress.streetName || '';
                updateData.buildingName = localAddress.buildingEstateName || '';
                updateData.addrLevel = localAddress.level || '';
                updateData.addrUnit = localAddress.unit || '';
                updateData.foreignAddressLine1 = '';
                updateData.foreignAddressLine2 = '';
            }

            // 백엔드에서 localAddress/foreignAddress 객체를 기대할 가능성에 대비해 함께 전달
            // MY인 경우 Entity 스타일 주소 필드도 포함 (CODE_NAME으로 변환된 값)
            updateData.localAddress = localAddress;
            updateData.foreignAddress = foreignAddress;
            
            console.log('📤 [handleSave] 최종 localAddress 전달:', localAddress);
            console.log('📤 [handleSave] 최종 foreignAddress 전달:', foreignAddress);
        }
        
        // 공통 필드
        if (contactInfoData.value) {
            const rawEmails = contactInfoData.value.emails || [];
            const primaryEmail = (rawEmails[0] || '').trim();
            const secondaryEmail = (rawEmails[1] || '').trim();

            updateData.email = primaryEmail || '';
            updateData.emails = [primaryEmail, secondaryEmail].filter((email) => email !== '');

            updateData.emailMainYn = updateData.emails.map((_, index) => index === 0);
            updateData.emailSyncYn = updateData.emails.map((_, index) => {
                if (isManualRegistrant.value) return 0;
                return index === 0 ? 1 : 0;
            });

            currentEmailValue.value = updateData.email;
            
            const mobileNos = contactInfoData.value.mobileNos?.filter((m: string) => m.trim() !== '') || [];
            if (mobileNos.length > 0) {
                updateData.mobileNo = mobileNos[0];
            }
            
            // Country Code와 Area Code 추가 (Individual과 Corporate 모두)
            if (contactInfoData.value.countryCode !== undefined) {
                updateData.countryCode = contactInfoData.value.countryCode || '';
                console.log('✅ [handleSave] Country Code 업데이트:', {
                    isIndividual: contactStore.isIndividual,
                    isCorporate: contactStore.isCorporate,
                    countryCode: updateData.countryCode
                });
            }
            
            if (contactInfoData.value.areaCode !== undefined) {
                updateData.areaCode = contactInfoData.value.areaCode || '';
                console.log('✅ [handleSave] Area Code 업데이트:', {
                    isIndividual: contactStore.isIndividual,
                    isCorporate: contactStore.isCorporate,
                    areaCode: updateData.areaCode
                });
            }
            
            if (contactInfoData.value.note !== undefined) {
                updateData.note = contactInfoData.value.note;
            }
            
            // Alternative Address 데이터 추가 - 기존 Residential Address의 ADDRESS_TYPE_CD를 사용
            if (contact && !registerAddrCtx) {
                const addressType = contact.ADDRESS_TYPE_CD || 'B07001'; // 기존 Residential Address의 ADDRESS_TYPE_CD 사용
                
                // MY인지 확인
                const sessionStore = useSessionStore();
                const customerInfo = sessionStore.user?.customer || null;
                const jurisdiction = customerInfo?.jurisdiction || contact.JURISDICTION || '';
                const isMalaysia = jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
                const hideSvcBiz = hideServiceBusinessOnContactMenu.value;

                if (hideSvcBiz) {
                    (updateData as any).skipServiceBusinessDedicatedProfileColumnSync = true;
                }

                // contactInfoData.value.altLocalAddress를 우선 사용 (ContactInfoForm에서 최신 값)
                // 없으면 contact 객체에서 읽기
                let altLocalAddress: any = null;
                if (!hideSvcBiz) {
                if (contactInfoData.value?.altLocalAddress) {
                    // ContactInfoForm에서 최신 altLocalAddress 사용 (CODE_NAME으로 변환된 값)
                    altLocalAddress = { ...contactInfoData.value.altLocalAddress };
                    console.log('✅ [handleSave] contactInfoData.value.altLocalAddress 사용:', altLocalAddress);
                } else {
                    // 기존 방식: contact 객체에서 읽기
                    altLocalAddress = {
                        postalCode: contact.ALT_POSTAL_CODE || '',
                        blockHouseNo: contact.ALT_BLOCK_NO || '',
                        streetName: contact.ALT_STREET_NAME || '',
                        buildingEstateName: contact.ALT_BUILDING_NAME || '',
                        level: contact.ALT_ADDR_LEVEL || '',
                        unit: contact.ALT_ADDR_UNIT || '',
                    };
                    
                    // MY인 경우 Entity 스타일 주소 필드 추가
                    if (isMalaysia) {
                        altLocalAddress.addressLine1 = (contact as any).ALT_ADDRESS_LINE_1 || (contactInfoData.value as any)?.ALT_ADDRESS_LINE_1 || '';
                        altLocalAddress.addressLine2 = (contact as any).ALT_ADDRESS_LINE_2 || (contactInfoData.value as any)?.ALT_ADDRESS_LINE_2 || '';
                        altLocalAddress.addressLine3 = (contact as any).ALT_ADDRESS_LINE_3 || (contactInfoData.value as any)?.ALT_ADDRESS_LINE_3 || '';
                        altLocalAddress.country = (contact as any).ALT_COUNTRY || (contactInfoData.value as any)?.ALT_COUNTRY || '';
                        altLocalAddress.state = (contact as any).ALT_STATE || (contactInfoData.value as any)?.ALT_STATE || '';
                        altLocalAddress.city = (contact as any).ALT_CITY || (contactInfoData.value as any)?.ALT_CITY || '';
                        altLocalAddress.postcode = (contact as any).ALT_POSTCODE || (contactInfoData.value as any)?.ALT_POSTCODE || '';
                    }
                    console.log('⚠️ [handleSave] contact 객체에서 altLocalAddress 읽기:', altLocalAddress);
                }
                
                const altForeignAddress = {
                    addressLine1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || '',
                    addressLine2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || '',
                };

                // ALT_ADDRESS_TYPE_CD는 저장하지 않음 (ADDRESS_TYPE_CD를 그대로 사용)

                if (addressType === 'B07002') {
                    // Service/Foreign Address
                    updateData.altForeignAddressLine1 = altForeignAddress.addressLine1;
                    updateData.altForeignAddressLine2 = altForeignAddress.addressLine2;
                    updateData.altPostalCode = '';
                    updateData.altBlockNo = '';
                    updateData.altStreetName = '';
                    updateData.altBuildingName = '';
                    updateData.altAddrLevel = '';
                    updateData.altAddrUnit = '';
                } else {
                    // Residential/Local Address
                    updateData.altPostalCode = altLocalAddress.postalCode;
                    updateData.altBlockNo = altLocalAddress.blockHouseNo;
                    updateData.altStreetName = altLocalAddress.streetName;
                    updateData.altBuildingName = altLocalAddress.buildingEstateName;
                    updateData.altAddrLevel = altLocalAddress.level;
                    updateData.altAddrUnit = altLocalAddress.unit;
                    updateData.altForeignAddressLine1 = '';
                    updateData.altForeignAddressLine2 = '';
                }

                // 백엔드에서 altLocalAddress/altForeignAddress 객체를 기대할 가능성에 대비해 함께 전달
                // Entity_my의 alternateAddress 형태로 저장 (Service Address와 동일)
                updateData.altLocalAddress = altLocalAddress;
                updateData.altForeignAddress = altForeignAddress;

                // CONTACT_JSON_DATA 배열 주소 편집값 전달 (인덱스별 수정 반영)
                if (!hideSvcBiz) {
                if (Array.isArray((contactInfoData.value as any)?.serviceAddressList)) {
                    (updateData as any).serviceAddressList = (contactInfoData.value as any).serviceAddressList;
                }
                if (Array.isArray((contactInfoData.value as any)?.businessAddressList)) {
                    (updateData as any).businessAddressList = (contactInfoData.value as any).businessAddressList;
                }
                }
                } // !hideSvcBiz (alt / service / business)

                const useBranch = !!(contact as any)?.USE_BRANCH_ADDRESSES;
                const useFlatMulti =
                    hideSvcBiz && !useBranch && isMalaysia;
                const resDraft = useBranch || useFlatMulti
                    ? getResidentialBranchListForDetailSave()
                    : branchResidentialDraft.value.length > 0
                      ? branchResidentialDraft.value
                      : branchResidentialAddressList.value;

                if (useBranch) {
                    (updateData as any).useBranchAddresses = true;
                    (updateData as any).skipResidentialJsonDataWrite = true;
                    if (hideSvcBiz) {
                        (updateData as any).skipServiceBusinessBranchSave = true;
                    }
                    (updateData as any).residentialAddressList = (
                        Array.isArray(resDraft) ? resDraft : []
                    ).map((row) => ({ ...row }));
                    const branchLists = contactInfoFormRef.value?.getBranchAddressListsForSave?.();
                    if (!hideSvcBiz) {
                    if (branchLists) {
                        (updateData as any).serviceAddressList = branchLists.serviceAddressList.map(
                            (row: any) => ({ ...row }),
                        );
                        (updateData as any).businessAddressList = branchLists.businessAddressList.map(
                            (row: any) => ({ ...row }),
                        );
                        if (contactInfoData.value) {
                            (contactInfoData.value as any).serviceAddressList = branchLists.serviceAddressList;
                            (contactInfoData.value as any).businessAddressList = branchLists.businessAddressList;
                        }
                    } else {
                        (updateData as any).serviceAddressList = Array.isArray(
                            (contactInfoData.value as any)?.serviceAddressList,
                        )
                            ? (contactInfoData.value as any).serviceAddressList.map((row: any) => ({ ...row }))
                            : [];
                        (updateData as any).businessAddressList = Array.isArray(
                            (contactInfoData.value as any)?.businessAddressList,
                        )
                            ? (contactInfoData.value as any).businessAddressList.map((row: any) => ({ ...row }))
                            : [];
                    }
                    }
                } else if (useFlatMulti) {
                    (updateData as any).saveContactListFlatAddressList = true;
                    (updateData as any).skipResidentialJsonDataWrite = true;
                    (updateData as any).skipContactListAddressSave = true;
                    (updateData as any).residentialAddressList = (
                        Array.isArray(resDraft) ? resDraft : []
                    ).map((row) => ({ ...row }));
                    if (Array.isArray(resDraft) && resDraft.length > 0) {
                        const primary = resDraft[0];
                        if (primary && updateData.localAddress) {
                            updateData.localAddress = {
                                ...updateData.localAddress,
                                addressLine1: primary.street01 || primary.addressLine1 || '',
                                addressLine2: primary.street02 || primary.addressLine2 || '',
                                addressLine3: primary.street03 || primary.addressLine3 || '',
                                country: primary.country || primary.countryCode || '',
                                state: primary.state || primary.stateCode || '',
                                city: primary.city || primary.cityCode || '',
                                postcode:
                                    primary.post_code || primary.postcode || primary.postalCode || '',
                                post_code:
                                    primary.post_code || primary.postcode || primary.postalCode || '',
                                address_id:
                                    primary.address_id ?? primary.ADDRESS_ID ?? primary.addressId,
                                addressId:
                                    primary.address_id ?? primary.ADDRESS_ID ?? primary.addressId,
                                ADDRESS_ID:
                                    primary.address_id ?? primary.ADDRESS_ID ?? primary.addressId,
                            };
                        }
                    }
                } else if (isMalaysia) {
                    (updateData as any).skipResidentialJsonDataWrite = true;
                    const resArr = (contact as any)?.residential_address;
                    const resRow = Array.isArray(resArr) && resArr.length > 0 ? resArr[0] : null;
                    const addrId = resRow?.address_id ?? resRow?.ADDRESS_ID ?? resRow?.addressId;
                    if (addrId != null && updateData.localAddress) {
                        updateData.localAddress = {
                            ...updateData.localAddress,
                            address_id: addrId,
                            addressId: addrId,
                            ADDRESS_ID: addrId,
                        };
                    }
                }

                if (useBranch && Array.isArray(resDraft) && resDraft.length > 0) {
                    const primary = resDraft[0];
                    if (primary) {
                        updateData.localAddress = {
                            addressLine1: primary.street01 || primary.addressLine1 || '',
                            addressLine2: primary.street02 || primary.addressLine2 || '',
                            addressLine3: primary.street03 || primary.addressLine3 || '',
                            country: primary.country || primary.countryCode || '',
                            state: primary.state || primary.stateCode || '',
                            city: primary.city || primary.cityCode || '',
                            postcode: primary.post_code || primary.postcode || primary.postalCode || '',
                            post_code: primary.post_code || primary.postcode || primary.postalCode || '',
                            countryCode: primary.countryCode || primary.country || '',
                            stateCode: primary.stateCode || primary.state || '',
                            cityCode: primary.cityCode || primary.city || '',
                            postcodeCode: primary.postcodeCode || primary.post_code || primary.postcode || '',
                        };
                    }
                }
            }
        }
        
        if (!registerAddrCtx) {
            const entityCdForSave = String(props.entityCd || '').trim();
            if (entityCdForSave) {
                (updateData as any).entityCd = entityCdForSave;
                (updateData as any).branchEntityCd = entityCdForSave;
                if (props.rememberedPosition?.positionId) {
                    (updateData as any).positionCd = props.rememberedPosition.positionId;
                }
                if (props.rememberedPosition?.positionName) {
                    (updateData as any).positionName = props.rememberedPosition.positionName;
                }
            }
        }

        console.log('📤 [handleSave] 최종 updateData:', {
            isIndividual: contactStore.isIndividual,
            isCorporate: contactStore.isCorporate,
            updateData,
            addressTypeCd: updateData.addressTypeCd,
            contactAUDITOR_YN: contact.AUDITOR_YN,
            contactPublicAccountingEntities: (contact as any).publicAccountingEntities,
            addressData: addressData.value
        });
        
        if (registerAddrCtx) {
            const addressRefs = getEntityRegisterAddressSaveRefs();
            await saveMyRegisterContactProfileAndAddresses({
                entityCd: registerAddrCtx.entityCd,
                contactCd: contactId.value,
                positionCd: registerAddrCtx.positionCd,
                positionName: props.rememberedPosition?.positionName || '',
                categoryCd: registerAddrCtx.categoryCd || entityRegisterCategoryCdOnDetail.value,
                contactUpdate: updateData as UpdateContactData & Record<string, unknown>,
                refs: addressRefs,
            });
        } else {
            await contactStore.updateContact(contactId.value, updateData);
        }

        if (hasEntityOfficerChanges.value && entityOfficerForm.value?.officerIdx) {
            const entityCdForOfficer = String(
                props.entityCd || entityOfficerForm.value.entityCd || '',
            ).trim();
            const form = entityOfficerForm.value;
            const rolePayload: {
                officerIdx: number | string;
                occupation?: string;
                secretaryLicenseNo?: string;
                secretaryCertificateNo?: string;
                firmNo?: string;
            } = {
                officerIdx: form.officerIdx!,
            };

            if (form.positionGroup === 'director' || form.positionGroup === 'manager') {
                rolePayload.occupation = form.occupation ?? '';
            } else if (form.positionGroup === 'secretary') {
                rolePayload.occupation = form.occupation ?? '';
                rolePayload.secretaryLicenseNo = form.secretaryLicenseNo ?? '';
                rolePayload.secretaryCertificateNo = form.secretaryCertificateNo ?? '';
            } else if (form.positionGroup === 'auditor') {
                rolePayload.firmNo = form.firmNo ?? '';
            }

            if (entityCdForOfficer) {
                await entityMyApi.updateEntityOfficerRoleFields(entityCdForOfficer, rolePayload);
                originalEntityOfficerForm.value = { ...form };
            }
        }

        if (hasEntityBeneficialOwnerChanges.value && entityBeneficialOwnerForm.value?.ctrlIdx) {
            const entityCdForBo = String(
                props.entityCd || entityBeneficialOwnerForm.value.entityCd || '',
            ).trim();
            const boForm = entityBeneficialOwnerForm.value;
            if (entityCdForBo) {
                await entityMyApi.updateEntityBeneficialOwnerRoleFields(entityCdForBo, {
                    ctrlIdx: boForm.ctrlIdx!,
                    occupation: boForm.occupation ?? '',
                });
                originalEntityBeneficialOwnerForm.value = { ...boForm };
            }
        }
        
        // 레지스터 등록 체크박스가 체크되어 있고 rememberedPosition이 있으면 레지스터에 등록
        if (registerActivityHistory.value && props.rememberedPosition) {
            emit('register', {
                contactId: contactId.value,
                positionId: props.rememberedPosition.positionId,
                positionName: props.rememberedPosition.positionName
            });
        }
        
        await alert({
            title: t('contact.Success') || 'Success',
            message: t('contact.Contact updated successfully') || 'Contact updated successfully',
            type: 'success'
        });
        
        // 데이터 새로고침
        await loadContact();
        
        // updated 이벤트 emit (수정된 Contact 데이터 함께 전달)
        emit('updated', contactStore.currentContact);
        
        // 모달 모드일 때 저장 후 모달 닫기 (부모 컴포넌트에서 처리하도록 back 이벤트 emit)
        if (props.isModal) {
            emit('back');
        }
        
        // 저장 후 체크박스 초기화
        registerActivityHistory.value = false;
    } catch (error: any) {
        console.error('❌ [handleSave] Update failed:', error);
        
        const errorMessage = error?.response?.data?.error?.message 
            || error?.response?.data?.message 
            || error?.message 
            || t('contact.Failed to update contact') || 'Failed to update contact';
        
        await alert({
            title: t('contact.Error') || 'Error',
            message: errorMessage,
            type: 'error'
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Guarded Save click: prevent confirm dialog from opening when permission is denied
const onSaveClickGuard = createGuardedAction('/Contact', 'Save', () => handleSave());

const handleDelete = async () => {
    await contactStore.deleteContact(contactId.value);
    deleteDialog.value = false;
    router.push('/Contact');
};

// 주소 데이터 - ref로 변경하여 직접 수정 가능하도록
const addressData = ref<{
    addressType: 'B07001' | 'B07002';
    localAddress?: {
        postalCode: string;
        blockHouseNo: string;
        streetName: string;
        buildingEstateName: string;
        level: string;
        unit: string;
        // MY인 경우 Entity 스타일 주소 필드 (CODE_NAME으로 저장)
        addressLine1?: string;
        addressLine2?: string;
        addressLine3?: string;
        country?: string; // CODE_NAME
        state?: string; // CODE_NAME
        city?: string; // CODE_NAME
        postcode?: string; // CODE_NAME
        post_code?: string; // CODE_NAME (백엔드에서 사용)
        // 바인딩용 CODE (GUID)
        countryCode?: string;
        stateCode?: string;
        cityCode?: string;
        postcodeCode?: string;
    };
    foreignAddress?: {
        addressLine1: string;
        addressLine2: string;
    };
} | null>(null);

// Contact 정보 데이터 - ref로 변경하여 직접 수정 가능하도록
const contactInfoFormRef = ref<InstanceType<typeof ContactInfoForm> | null>(null);

/** ContactAddressForm / NoSyncContactAddressForm — Registered branch Save flush */
const contactAddressFormRef = ref<{
    getResidentialBranchListForSave?: () => any[] | null;
} | null>(null);

function getEntityRegisterAddressSaveRefs(): EntityDirectorAddressSaveRefs | null {
    if (!addressData.value || !contactInfoData.value) return null;

    const useBranch = useBranchAddressesOnDetail.value;
    const isOfficerIndividual = registerOfficerAddressModeOnDetail.value && contactStore.isIndividual;

    if (isOfficerIndividual) {
        return {
            addressData: addressData.value as Record<string, unknown>,
            contactInfoData: contactInfoData.value as Record<string, unknown>,
            useBranchAddresses: useBranch,
            getResidentialBranchListForSave: () =>
                sliceOfficerAddressSaveList(getResidentialBranchListForDetailSave()),
            getBranchAddressListsForSave: () => {
                const lists = contactInfoFormRef.value?.getBranchAddressListsForSave?.();
                if (!lists) {
                    return { serviceAddressList: [], businessAddressList: [] };
                }
                return {
                    serviceAddressList: sliceOfficerAddressSaveList(lists.serviceAddressList) ?? [],
                    businessAddressList: [],
                };
            },
        };
    }

    return {
        addressData: addressData.value as Record<string, unknown>,
        contactInfoData: contactInfoData.value as Record<string, unknown>,
        useBranchAddresses: useBranch,
        getResidentialBranchListForSave: () =>
            contactAddressFormRef.value?.getResidentialBranchListForSave?.()
            ?? getResidentialBranchListForDetailSave(),
        getBranchAddressListsForSave: () => {
            const lists = contactInfoFormRef.value?.getBranchAddressListsForSave?.();
            if (!lists) return null;
            return {
                serviceAddressList: lists.serviceAddressList,
                businessAddressList: showRegisterBusinessAddressOnDetail.value
                    ? lists.businessAddressList
                    : [],
            };
        },
    };
}

function getResidentialBranchListForDetailSave(): any[] {
    const fromForm = contactAddressFormRef.value?.getResidentialBranchListForSave?.();
    if (fromForm != null) {
        return fromForm.map((r) => ({ ...r }));
    }
    if (branchResidentialDraft.value.length > 0) {
        return branchResidentialDraft.value.map((r) => ({ ...r }));
    }
    return [...branchResidentialAddressList.value];
}

/** MY Corporate branch — Registered 삭제 PUT 시 Service/Business 목록 포함 */
function resolveBranchListsForPersist(residentialList: any[]): MyCorporateBranchAddressLists {
    const branch = contactInfoFormRef.value?.getBranchAddressListsForSave?.();
    // Delete 시 전달된 residentialList(삭제 반영 후)를 우선 — form ref는 아직 갱신 전일 수 있음
    const residential = Array.isArray(residentialList)
        ? residentialList.map((r) => ({ ...r }))
        : (contactAddressFormRef.value?.getResidentialBranchListForSave?.() ?? []).map((r) => ({ ...r }));
    return {
        residentialAddressList: residential,
        serviceAddressList: (branch?.serviceAddressList ?? []).map((r) => ({ ...r })),
        businessAddressList: (branch?.businessAddressList ?? []).map((r) => ({ ...r })),
    };
}

const contactInfoData = ref<{
    countryCode: string;
    areaCode: string;
    mobileNos: string[];
    emails: string[];
    emailMainYn?: boolean[];
    emailSyncYn?: (boolean | number)[];
    note: string;
    CONTACT_REGISTER_TYPE?: string;
    contactRegisterType?: string;
    altLocalAddress?: any; // Service Address용 (CONTACT_JSON_DATA의 service_address로 저장)
    serviceAddressList?: any[];
    businessAddressList?: any[];
} | null>(null);

const isSyncingContactData = ref(false);
const originalEmailValue = ref('');
const currentEmailValue = ref('');

// ── SPC 주소 비교·통합 다이얼로그 (MY는 운영 포함 마운트; 상단 compare 버튼만 isDevCompareEnabled) ──
const isDevCompareEnabled = isLocal() || isDevelopment();
const showAddressCompareDialog = ref(false);
const addressCompareGroups = ref<Record<string, Array<Record<string, any>>>>({});
const addressCompareSpcUi = ref(false);
const addressCompareDialogMode = ref<'compare' | 'resync'>('compare');
const addressCompareInitialGroupTab = ref<SpcAddressConsolidationGroupTab | null>(null);

const RESIDENTIAL_MULTI_SPC_HINT = 'Multiple residential addresses exist for this contact.';
const SERVICE_MULTI_SPC_HINT = 'Multiple service addresses exist for this contact.';
const BUSINESS_MULTI_SPC_HINT = 'Multiple business addresses exist for this contact.';

/** MY: SPC 번들 기준 다건 힌트 (MY 관할이면 환경 무관 갱신) */
const showResidentialMultiHint = ref(false);
const showServiceMultiHint = ref(false);
const showBusinessMultiHint = ref(false);
let spcHintRefreshSeq = 0;
/** SPC 힌트 watch·다이얼로그 닫힘 등 연속 트리거를 한 번으로 묶어 깜빡임·포커스 이탈 완화 */
const spcHintRefreshDebounceMs = 220;
const spcHintRefreshDebounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);

function isConsolidationMarkerOnAddrObject(o: unknown): boolean {
    if (!o || typeof o !== 'object' || Array.isArray(o)) return false;
    const v = (o as Record<string, unknown>).Consolidation;
    if (v === 1 || v === true || v === '1') return true;
    const n = Number(v);
    return Number.isFinite(n) && n === 1;
}

function parseAddrJsonField(raw: unknown): Record<string, unknown> | null {
    if (raw == null) return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (!t) return null;
        try {
            const o = JSON.parse(t);
            if (o && typeof o === 'object' && !Array.isArray(o)) return o as Record<string, unknown>;
        } catch {
            /* ignore */
        }
    }
    return null;
}

/** Saved profile already marked consolidated (column JSON or nested CONTACT_JSON_DATA * address). */
function contactAddrKindConsolidatedForHints(contact: any, kind: 'residential' | 'service' | 'business'): boolean {
    const j = parseAddrJsonField(contact?.CONTACT_JSON_DATA ?? contact?.contact_json_data ?? null);
    const nestedKey =
        kind === 'residential' ? 'residential_address' : kind === 'service' ? 'service_address' : 'business_address';
    const nestedRaw = j?.[nestedKey];
    const nestedObj =
        parseAddrJsonField(nestedRaw) ??
        (typeof nestedRaw === 'object' && nestedRaw && !Array.isArray(nestedRaw)
            ? (nestedRaw as Record<string, unknown>)
            : null);
    if (isConsolidationMarkerOnAddrObject(nestedObj)) return true;

    const colRaw =
        kind === 'residential'
            ? contact?.RESIDENTIAL_ADDR_JSON_DATA ?? contact?.residential_addr_json_data
            : kind === 'service'
              ? contact?.SERVICE_ADDR_JSON_DATA ?? contact?.service_addr_json_data
              : contact?.BUSINESS_ADDR_JSON_DATA ?? contact?.business_addr_json_data;
    return isConsolidationMarkerOnAddrObject(parseAddrJsonField(colRaw));
}

function applySpcMultiAddressHintsIfChanged(res: boolean, svc: boolean, bus: boolean) {
    if (showResidentialMultiHint.value !== res) showResidentialMultiHint.value = res;
    if (showServiceMultiHint.value !== svc) showServiceMultiHint.value = svc;
    if (showBusinessMultiHint.value !== bus) showBusinessMultiHint.value = bus;
}

function scheduleRefreshSpcMultiAddressHintsFromCompare() {
    if (spcHintRefreshDebounceTimer.value != null) {
        clearTimeout(spcHintRefreshDebounceTimer.value);
    }
    spcHintRefreshDebounceTimer.value = setTimeout(() => {
        spcHintRefreshDebounceTimer.value = null;
        void refreshSpcMultiAddressHintsFromCompare();
    }, spcHintRefreshDebounceMs);
}

/** 세션 또는 컨택트 관할이 MY일 때 (상세 Residential SPC 힌트·백엔드 MY 주소와 정합). */
const isMyJurisdictionDetail = computed(() => {
    const customerInfo = sessionStore.user?.customer || null;
    const sessionJ = String(customerInfo?.jurisdiction || '').trim();
    const contactJ = String(contactStore.currentContact?.JURISDICTION || '').trim();
    const u = (sessionJ || contactJ).toUpperCase();
    if (u === 'MY' || u === 'MALAYSIA') return true;
    return !!(contactStore.currentContact as any)?.USE_BRANCH_ADDRESSES;
});

/** MY Individual Detail만 SPC 다건 힌트·카드 내 re-sync (MY Corporate Detail은 숨김) */
const showSpcMultiAddressHintsOnDetail = computed(
    () =>
        isMyJurisdictionDetail.value &&
        contactStore.isIndividual &&
        !contactStore.isCorporate,
);

const showResidentialMultiHintOnDetail = computed(
    () => showResidentialMultiHint.value && showSpcMultiAddressHintsOnDetail.value,
);
const showServiceMultiHintOnDetail = computed(
    () => showServiceMultiHint.value && showSpcMultiAddressHintsOnDetail.value,
);
const showBusinessMultiHintOnDetail = computed(
    () => showBusinessMultiHint.value && showSpcMultiAddressHintsOnDetail.value,
);

function applyBranchMultiAddressHintsFromContact(contact: any) {
    if (!contact?.USE_BRANCH_ADDRESSES) return;
    const cat = String(
        contact.CATEGORY_CD || contact.category_cd || contact.categoryCd || '',
    )
        .trim()
        .toUpperCase();
    if (cat === 'B09001') {
        applySpcMultiAddressHintsIfChanged(false, false, false);
        return;
    }
    const resCount = Array.isArray(contact.residential_address) ? contact.residential_address.length : 0;
    const svcCount = Array.isArray(contact.service_address) ? contact.service_address.length : 0;
    const busCount = Array.isArray(contact.business_address) ? contact.business_address.length : 0;
    applySpcMultiAddressHintsIfChanged(resCount > 1, svcCount > 1, busCount > 1);
}

/** branch API → ContactInfoForm 목록 필드 */
function branchAddressListFromContact(contact: any, key: 'service_address' | 'business_address'): any[] {
    const top = (contact as any)?.[key];
    if (Array.isArray(top)) return top.map((x: any) => ({ ...x }));
    try {
        const raw = (contact as any)?.CONTACT_JSON_DATA ?? (contact as any)?.contact_json_data;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw && typeof raw === 'object' ? raw : {};
        const legacy = parsed?.[key];
        if (Array.isArray(legacy)) return legacy.map((x: any) => ({ ...x }));
    } catch {
        /* ignore */
    }
    return [];
}

/** MY 관할 — 폼·콜백의 re-sync 경로. Residential 위 툴바는 `showLocalAddressConsolidationToolbar`. */
const isMyReSyncAddressEnabled = computed(() => isMyJurisdictionDetail.value);

/** MY re-sync 상단 버튼 — 다건 힌트가 있을 때만 (통합 완료·Consolidation 마커면 힌트가 꺼져 숨김). */
const showMySpcAddressConsolidationReSyncToolbar = computed(
    () =>
        isMyReSyncAddressEnabled.value &&
        showSpcMultiAddressHintsOnDetail.value &&
        (showResidentialMultiHint.value || showServiceMultiHint.value || showBusinessMultiHint.value),
);

/** Residential 위 Address Consolidation 툴바 — `env === 'local'` 일 때만 (개발·운영 숨김) */
const showLocalAddressConsolidationToolbar = computed(
  () => isLocal() && (isMyReSyncAddressEnabled.value || isDevCompareEnabled),
);

/** 빈 툴바 방지: MY re-sync·DEV compare 중 하나라도 노출될 때만 래퍼 표시 */
const showLocalAddressConsolidationToolbarRow = computed(
    () =>
        showLocalAddressConsolidationToolbar.value &&
        (showMySpcAddressConsolidationReSyncToolbar.value || isDevCompareEnabled),
);

async function refreshSpcMultiAddressHintsFromCompare() {
    const seq = ++spcHintRefreshSeq;
    // 요청 전에 힌트를 false로 두지 않음(await 구간 깜빡임·레이아웃 시프트 방지)

    if (!isMyJurisdictionDetail.value) {
        applySpcMultiAddressHintsIfChanged(false, false, false);
        return;
    }

    const c = contactStore.currentContact as any;
    if (!c?.CONTACT_CD) {
        applySpcMultiAddressHintsIfChanged(false, false, false);
        return;
    }

    if (contactStore.isCorporate) {
        applySpcMultiAddressHintsIfChanged(false, false, false);
        return;
    }

    if (c.USE_BRANCH_ADDRESSES) {
        applyBranchMultiAddressHintsFromContact(c);
        return;
    }

    try {
        const { groups } = await fetchSpcContactAddressBundle();
        if (seq !== spcHintRefreshSeq) return;

        const nRes = countDistinctSpcAddressDisplayLines(groups.Residential);
        const nSvc = countDistinctSpcAddressDisplayLines(groups.Service);
        const nBus = countDistinctSpcAddressDisplayLines(groups['Business / BO']);

        const resCon = contactAddrKindConsolidatedForHints(c, 'residential');
        const svcCon = contactAddrKindConsolidatedForHints(c, 'service');
        const busCon = contactAddrKindConsolidatedForHints(c, 'business');

        applySpcMultiAddressHintsIfChanged(
            nRes >= 2 && !resCon,
            nSvc >= 2 && !svcCon,
            nBus >= 2 && !busCon,
        );
    } catch {
        if (seq !== spcHintRefreshSeq) return;
        // API 실패 시 기존 힌트 유지(불필요한 토글로 포커스·레이아웃 흔들림 방지)
    }
}

/** 주소 객체에 의미 있는 필드(street/postcode/city/state/country)가 하나라도 있는지. */
function isAddressMeaningful(a: any): boolean {
  if (!a || typeof a !== 'object') return false;
  const fields = [
    a.street1, a.street01, a.address1, a.addressLine1,
    a.street2, a.street02, a.address2, a.addressLine2,
    a.street3, a.street03, a.address3, a.addressLine3,
    a.postcode, a.post_code, a.zip,
    a.city, a.state, a.country,
  ];
  return fields.some((v: any) => v != null && String(v).trim() !== '');
}

function isCountryOnlyAddress(a: any): boolean {
  if (!a || typeof a !== 'object') return false;
  const hasStreet = [
    a.street1, a.street01, a.address1, a.addressLine1,
    a.street2, a.street02, a.address2, a.addressLine2,
    a.street3, a.street03, a.address3, a.addressLine3,
  ].some((v: any) => v != null && String(v).trim() !== '');
  const hasPostCode = String(a.postcode ?? a.post_code ?? a.zip ?? a.postalCode ?? '').trim() !== '';
  const hasCity = String(a.city ?? '').trim() !== '';
  const hasState = String(a.state ?? '').trim() !== '';
  const hasCountry = String(a.country ?? '').trim() !== '';
  return hasCountry && !hasStreet && !hasPostCode && !hasCity && !hasState;
}

/** raw JSON string / object 안전 파싱. */
function _parseJsonSafe(raw: any): any {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function unwrapMigAddressByType(addrType: string, parsed: any): any | null {
  if (!parsed || typeof parsed !== 'object') return null;
  if (addrType === 'M04001') return parsed.residential_address ?? null;
  if (addrType === 'M04002') return parsed.service_address ?? null;
  if (addrType === 'M04003') return parsed.business_address ?? null;
  return null;
}

/** SPC 주소 API 한 번 호출 — compare/score 없이도 MY 힌트용으로 M04001 원시 행 수를 함께 반환. */
async function fetchSpcContactAddressBundle(): Promise<{
  groups: Record<string, Array<Record<string, any>>>;
  residentialM04001ApiRowCount: number;
}> {
  const groups: Record<string, Array<Record<string, any>>> = {
    Residential: [],
    Service: [],
    'Business / BO': [],
  };
  const groupByType: Record<string, string> = {
    M04001: 'Residential',
    M04002: 'Service',
    M04003: 'Business / BO',
  };
  const contactCd = String((contactStore.currentContact as any)?.CONTACT_CD || '').trim();
  if (!contactCd) {
    return { groups: {}, residentialM04001ApiRowCount: 0 };
  }

  const res: any = await contactsApi.getDevSpcContactAddresses(contactCd);
  const data = (res as any)?.data || res;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  let residentialM04001ApiRowCount = 0;

  for (const row of rows) {
    const addrType = String(row?.ADDR_TYPE || row?.addrType || '').trim().toUpperCase();
    if (addrType === 'M04001') residentialM04001ApiRowCount += 1;

    const groupName = groupByType[addrType];
    if (!groupName) continue;
    const parsed = _parseJsonSafe(row?.ADDR_JSON_DATA ?? row?.addrJsonData);
    const addrObj = unwrapMigAddressByType(addrType, parsed);
    if (!isAddressMeaningful(addrObj)) continue;
    if (isCountryOnlyAddress(addrObj)) continue;
    const label = [
      row?.POSITION || row?.position || '',
      row?.SOURCE_SYSTEM || row?.sourceSystem || '',
      row?.SOURCE_CODE || row?.sourceCode || '',
    ].filter(Boolean).join(' | ');
    groups[groupName].push({
      ...addrObj,
      _label: label || `${groupName} (SPC)`,
      _source: 'MIG_L_GLOBAL_CONTACT_ADDRESS_SPC',
      _contactCd: row?.CONTACT_CD || row?.contactCd || null,
      _addrType: addrType,
      _positionCd: row?.POSITION_CD || row?.positionCd || null,
      _entityCd: row?.ENTITY_CD || row?.entityCd || null,
    });
  }
  for (const k of Object.keys(groups)) {
    if (groups[k].length === 0) delete groups[k];
  }
  return { groups, residentialM04001ApiRowCount };
}

async function collectSpcContactAddressGroups(): Promise<Record<string, Array<Record<string, any>>>> {
  const { groups } = await fetchSpcContactAddressBundle();
  return groups;
}

async function openAddressCompareSpc(
    mode: 'compare' | 'resync' = 'compare',
    initialGroup?: SpcAddressConsolidationGroupTab | null
) {
  try {
    addressCompareDialogMode.value = mode;
    addressCompareSpcUi.value = true;
    addressCompareInitialGroupTab.value = initialGroup ?? null;
    addressCompareGroups.value = await collectSpcContactAddressGroups();
    showAddressCompareDialog.value = true;
  } catch (e: any) {
    await alert({
      title: t('contact.Error') || 'Error',
      message: e?.message || 'Failed to load SPC migration addresses.',
      type: 'error',
    });
  }
}

watch(showAddressCompareDialog, (open) => {
  if (!open) {
    scheduleRefreshSpcMultiAddressHintsFromCompare();
    addressCompareInitialGroupTab.value = null;
  }
});

watch(
    () => ({
        contactCd: String((contactStore.currentContact as any)?.CONTACT_CD || '').trim(),
        my: isMyJurisdictionDetail.value,
        loading: contactStore.isLoading,
    }),
    ({ contactCd, my, loading }) => {
        if (loading) return;
        if (!contactCd || !my) {
            applySpcMultiAddressHintsIfChanged(false, false, false);
            return;
        }
        scheduleRefreshSpcMultiAddressHintsFromCompare();
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    if (spcHintRefreshDebounceTimer.value != null) {
        clearTimeout(spcHintRefreshDebounceTimer.value);
        spcHintRefreshDebounceTimer.value = null;
    }
});

/** MY Corporate branch — Registered 카드용 addressData 껍데기 (실데이터는 residential_address[]) */
function createBranchAddressDataShell(contact: any) {
    return {
        addressType: 'B07001' as const,
        CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        localAddress: {
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            country: '',
            state: '',
            city: '',
            postcode: '',
            post_code: '',
        },
        foreignAddress: {
            addressLine1: '',
            addressLine2: '',
        },
    };
}

/** API 로드 후 폼용 addressData / contactInfoData 1회 구성 (deep watch 제거 — branch N건 시 루프 방지) */
function hydrateDetailFromContact(contact: any | null) {
    if (!contact) {
        addressData.value = null;
        contactInfoData.value = null;
        return;
    }

    const useBranchHydrate = !!(contact as any).USE_BRANCH_ADDRESSES;
    if (useBranchHydrate) {
        addressData.value = createBranchAddressDataShell(contact) as any;
    }

    isSyncingContactData.value = true;
    try {
    // 세션의 JURISDICTION이 MY인지 확인
    const sessionStore = useSessionStore();
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = customerInfo?.jurisdiction || contact.JURISDICTION || '';
    const useBranchHydrateEarly = !!(contact as any).USE_BRANCH_ADDRESSES;
    const isMalaysia =
        jurisdiction?.toUpperCase() === 'MY' ||
        jurisdiction?.toUpperCase() === 'MALAYSIA' ||
        useBranchHydrateEarly;
    const categoryCd = String(contact.CATEGORY_CD || contact.categoryCd || '').trim().toUpperCase();
    const isIndividualContact = categoryCd !== 'B09001';
    const isContactListMenu = isContactListMyDetail.value;
    const useContactListFlatMultiHydrate =
        isContactListMenu && isIndividualContact && !useBranchHydrate;
    console.log('🔍 [ContactDetailPage] isMalaysia:', isMalaysia, 'jurisdiction:', jurisdiction);
    
    // MY인 경우 registeredAddress 사용, 아닌 경우 기존 방식
    let newAddressData: any = createBranchAddressDataShell(contact);

    // MY인 경우: L_CONTACT_ADDRESS(residential_address) 우선, Profile 폴백
    const registerType = contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '';
    const isManual = String(registerType).trim().toUpperCase() === 'A06002';
    
    let registeredAddr: any = null;
    if (isMalaysia && !useBranchHydrate) {
        if (useContactListFlatMultiHydrate) {
            const ra = (contact as any).residential_address;
            if (Array.isArray(ra) && ra.length > 0) {
                registeredAddr = pickPrimaryResidentialForDisplay(ra) || { ...ra[0] };
            } else {
                registeredAddr = pickResidentialForMyAddressForm(contact);
            }
        } else {
            registeredAddr = pickResidentialForMyAddressForm(contact);
        }
        const regFromApi = (contact as any).registeredAddress;
        if (registeredAddr && regFromApi && typeof regFromApi === 'object') {
            registeredAddr = {
                ...registeredAddr,
                state: String(registeredAddr.state || regFromApi.state || '').trim(),
                city: String(registeredAddr.city || regFromApi.city || '').trim(),
                country: String(
                    registeredAddr.country || regFromApi.country || ''
                ).trim(),
                stateCode: regFromApi.stateCode || registeredAddr.stateCode,
                cityCode: regFromApi.cityCode || registeredAddr.cityCode,
                postcodeCode: regFromApi.postcodeCode || registeredAddr.postcodeCode,
                countryCode: regFromApi.countryCode || registeredAddr.countryCode,
                postcode:
                    registeredAddr.postcode ||
                    registeredAddr.post_code ||
                    regFromApi.postcode ||
                    regFromApi.postalCode,
                post_code:
                    registeredAddr.post_code ||
                    registeredAddr.postcode ||
                    regFromApi.post_code ||
                    regFromApi.postcode,
            };
        }
        console.log('🔍 [ContactDetailPage] MY registeredAddr (RESIDENTIAL_ADDR_JSON_DATA 우선):', registeredAddr);
    }

    if (useBranchHydrate) {
        newAddressData = createBranchAddressDataShell(contact);
    } else if (isMalaysia && registeredAddr) {
        console.log('🔍 [ContactDetailPage] MY address data:', {
            registeredAddress: (contact as any).registeredAddress,
            residential_address: (contact as any).residential_address,
            residential_address_isArray: Array.isArray((contact as any).residential_address),
            registeredAddr,
            isManual,
            post_code: registeredAddr.post_code,
            postcode: registeredAddr.postcode
        });
        newAddressData = {
            addressType: 'B07001', // MY는 항상 Local Address
            // CONTACT_REGISTER_TYPE 포함 (A06002 확인용)
            CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
            contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
            localAddress: {
                // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
                addressLine1: registeredAddr.addressLine1 || registeredAddr.street01 || '',
                addressLine2: registeredAddr.addressLine2 || registeredAddr.street02 || '',
                addressLine3: registeredAddr.addressLine3 || registeredAddr.street03 || '',
                country: registeredAddr.country || registeredAddr.countryCode || '',
                state: registeredAddr.state || '',
                city: registeredAddr.city || '',
                postcode: registeredAddr.postcode || registeredAddr.post_code || registeredAddr.postalCode || '',
                post_code: registeredAddr.post_code || registeredAddr.postcode || registeredAddr.postalCode || '',
                countryCode: registeredAddr.countryCode || registeredAddr.country || '',
                stateCode: registeredAddr.stateCode || '',
                cityCode: registeredAddr.cityCode || '',
                postcodeCode: registeredAddr.postcodeCode || '',
                IsSync: registeredAddr.IsSync ?? registeredAddr.isSync,
            } as any,
            foreignAddress: {
                addressLine1: '',
                addressLine2: '',
            },
        };
        console.log('✅ [ContactDetailPage] newAddressData created:', {
            localAddress: newAddressData.localAddress,
            postcode: newAddressData.localAddress.postcode,
            post_code: (newAddressData.localAddress as any).post_code
        });
    } else {
        // MY가 아닌 경우: 기존 방식
        newAddressData = {
            addressType: contact.ADDRESS_TYPE_CD === 'B07002' ? 'B07002' : 'B07001',
            localAddress: {
                postalCode: contact.POSTAL_CODE || '',
                blockHouseNo: contact.BLOCK_NO || '',
                streetName: contact.STREET_NAME || '',
                buildingEstateName: contact.BUILDING_NAME || '',
                level: contact.ADDR_LEVEL || '',
                unit: contact.ADDR_UNIT || '',
            },
            foreignAddress: {
                addressLine1: contact.FOREIGN_ADDRESS_LINE_1 || '',
                addressLine2: contact.FOREIGN_ADDRESS_LINE_2 || '',
            },
        };
    }
    
    // branch: 항상 설정(Registered 카드 v-if). 그 외: 변경 시만
    if (useBranchHydrate) {
        addressData.value = newAddressData as any;
    } else if (!addressData.value || JSON.stringify(addressData.value) !== JSON.stringify(newAddressData)) {
        addressData.value = newAddressData as any;
    }

    // Contact 정보 데이터가 변경된 경우에만 업데이트 (무한 루프 방지)
    // L_CONTACT_EMAIL 테이블에서 조회한 이메일 목록 사용
    const contactEmailsRaw = (contact as any).CONTACT_EMAILS;
    const emailList = Array.isArray(contactEmailsRaw) ? contactEmailsRaw : [];
    const primaryEmail = contact.CONTACT_EMAIL ? String(contact.CONTACT_EMAIL).trim() : '';
    originalEmailValue.value = primaryEmail;
    currentEmailValue.value = primaryEmail;
    let emailsForDisplay: string[] = [];
    if (primaryEmail) {
        emailsForDisplay = [primaryEmail.trim(), ...emailList]
            .map((email: string) => String(email ?? '').trim())
            .filter((email: string, idx: number, arr: string[]) => email.length > 0 && arr.indexOf(email) === idx)
            .slice(0, 2);
    } else {
        emailsForDisplay = emailList.length > 0
            ? emailList
                .map((email: string) => String(email ?? '').trim())
                .filter((email: string) => email.length > 0)
                .slice(0, 2)
            : [''];
    }
    const emailMainYnSource = (contact as any).EMAIL_MAIN_YN || [];
    const emailSyncYnSource = (contact as any).EMAIL_SYNC_YN || [];
    const emailMainYn = emailsForDisplay.map((email: string, idx: number) => {
        if (!email || email.trim() === '') {
            return false;
        }
        const value = emailMainYnSource[idx];
        return value === true || value === 1 || value === '1';
    });
    const emailSyncYn = emailsForDisplay.map((email: string, idx: number) => {
        const value = emailSyncYnSource[idx];
        return value === true || value === 1 || value === '1';
    });
    
    const newContactInfoData: any = {
        countryCode: contact.COUNTRY_CD || '',
        areaCode: contact.AREA_CODE || '',
        mobileNos: contact.MOBILE_NO ? [contact.MOBILE_NO] : [''],
        emails: emailsForDisplay.slice(0, 2),
        emailMainYn: (emailMainYn.length > 0 ? emailMainYn : [true]).slice(0, 2),
        emailSyncYn: (emailSyncYn.length > 0 ? emailSyncYn : [true]).slice(0, 2),
        note: contact.NOTE || '',
        CONTACT_EMAIL: primaryEmail || '',
        // From Manual 체크를 위해 CONTACT_REGISTER_TYPE 전달
        CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        // ADDRESS_TYPE_CD 추가 (Alternative Address가 이를 사용)
        ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || 'B07001',
        addressTypeCd: contact.ADDRESS_TYPE_CD || 'B07001',
        // Alternative Address 필드 추가
        ALT_POSTAL_CODE: contact.ALT_POSTAL_CODE || '',
        ALT_BLOCK_NO: contact.ALT_BLOCK_NO || '',
        ALT_STREET_NAME: contact.ALT_STREET_NAME || '',
        ALT_BUILDING_NAME: contact.ALT_BUILDING_NAME || '',
        ALT_ADDR_LEVEL: contact.ALT_ADDR_LEVEL || '',
        ALT_ADDR_UNIT: contact.ALT_ADDR_UNIT || '',
        ALT_FOREIGN_ADDRESS_LINE_1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || '',
        ALT_FOREIGN_ADDRESS_LINE_2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || '',
        CONTACT_JSON_DATA: (contact as any).CONTACT_JSON_DATA || (contact as any).contact_json_data || null,
        SERVICE_ADDR_JSON_DATA:
            (contact as any).SERVICE_ADDR_JSON_DATA ?? (contact as any).service_addr_json_data ?? null,
        BUSINESS_ADDR_JSON_DATA:
            (contact as any).BUSINESS_ADDR_JSON_DATA ?? (contact as any).business_addr_json_data ?? null,
        USE_BRANCH_ADDRESSES: !!(contact as any).USE_BRANCH_ADDRESSES,
    };

    // 비-MY: CONTACT_JSON_DATA.service_address / business_address
    // MY: 아래 isMalaysia 분기에서 SERVICE_ADDR_JSON_DATA / BUSINESS_ADDR_JSON_DATA 로 채움
    try {
        const rawContactJson = (contact as any).CONTACT_JSON_DATA || (contact as any).contact_json_data || null;
        const parsedContactJson = typeof rawContactJson === 'string'
            ? JSON.parse(rawContactJson)
            : (rawContactJson || {});
        if (isMalaysia || useBranchHydrate) {
            newContactInfoData.serviceAddressList = [];
            newContactInfoData.businessAddressList = [];
        } else {
            newContactInfoData.serviceAddressList = filterContactJsonPrimaryOnly(parsedContactJson?.service_address);
            newContactInfoData.businessAddressList = filterContactJsonPrimaryOnly(parsedContactJson?.business_address);
        }
    } catch (contactJsonParseError) {
        console.warn('⚠️ [ContactDetailPage] CONTACT_JSON_DATA 파싱 실패 (배열 주소 초기화)', contactJsonParseError);
        newContactInfoData.serviceAddressList = [];
        newContactInfoData.businessAddressList = [];
    }
    
    // MY: Residential / Service / Business (branch 포함)
    if (isMalaysia || useBranchHydrate) {
        if (useBranchHydrate) {
            const branchResidential = (contact as any).residential_address;
            if (Array.isArray(branchResidential)) {
                newContactInfoData.residential_address =
                    branchResidential.length > 0
                        ? branchResidential.map((x: any) => ({ ...x }))
                        : [];
            } else if (branchResidential && typeof branchResidential === 'object') {
                newContactInfoData.residential_address = [{ ...branchResidential }];
            } else {
                newContactInfoData.residential_address = [];
            }
            newContactInfoData.serviceAddressList = branchAddressListFromContact(contact, 'service_address');
            newContactInfoData.businessAddressList = branchAddressListFromContact(contact, 'business_address');
            newContactInfoData.CONTACT_JSON_DATA = {
                service_address: newContactInfoData.serviceAddressList,
                business_address: newContactInfoData.businessAddressList,
            };
            applyBranchMultiAddressHintsFromContact(contact);
        } else if (useContactListFlatMultiHydrate) {
            const ra = (contact as any).residential_address;
            if (Array.isArray(ra)) {
                newContactInfoData.residential_address =
                    ra.length > 0 ? ra.map((x: any) => ({ ...x })) : [];
            } else if (ra && typeof ra === 'object') {
                newContactInfoData.residential_address = [{ ...ra }];
            } else {
                newContactInfoData.residential_address = [];
            }
            newContactInfoData.serviceAddressList = [];
            newContactInfoData.businessAddressList = [];
        } else {
        const residentialSrc = pickResidentialForMyAddressForm(contact);
        if (residentialSrc) {
            newContactInfoData.residential_address = residentialSrc;
            console.log('🔍 [ContactDetailPage] newContactInfoData.residential_address 설정 (컬럼·primary):', newContactInfoData.residential_address);
        }

        const svcRaw =
            (contact as any).SERVICE_ADDR_JSON_DATA ?? (contact as any).service_addr_json_data ?? null;
        const bizRaw =
            (contact as any).BUSINESS_ADDR_JSON_DATA ?? (contact as any).business_addr_json_data ?? null;
        newContactInfoData.serviceAddressList = myProfileAddrListPrimaryOrAll(svcRaw, 'service_address');
        newContactInfoData.businessAddressList = myProfileAddrListPrimaryOrAll(bizRaw, 'business_address');
        }

        let serviceSource: any = null;
        if (Array.isArray(newContactInfoData.serviceAddressList) && newContactInfoData.serviceAddressList.length > 0) {
            const primaryServiceAddress = newContactInfoData.serviceAddressList.find((addr: any) =>
                isPrimaryAddressDisplayFlag(addr?.isPrimaryAddress ?? addr?.is_primary_address)
            );
            serviceSource = primaryServiceAddress
                ? { ...primaryServiceAddress }
                : { ...newContactInfoData.serviceAddressList[0] };
            console.log('🔍 [ContactDetailPage] SERVICE_ADDR_JSON_DATA 기반 Service:', serviceSource);
        }

        if (serviceSource) {
            console.log('🔍 [ContactDetailPage] ====== Service 원본 → altLocalAddress ======');
            newContactInfoData.altLocalAddress = {
                addressLine1: serviceSource.addressLine1 || serviceSource.street01 || '',
                addressLine2: serviceSource.addressLine2 || serviceSource.street02 || '',
                addressLine3: serviceSource.addressLine3 || serviceSource.street03 || '',
                country: serviceSource.country || '',
                state: serviceSource.state || '',
                city: serviceSource.city || '',
                postcode: serviceSource.postcode || serviceSource.post_code || serviceSource.postalCode || '',
                post_code: serviceSource.post_code || serviceSource.postcode || serviceSource.postalCode || '',
                countryCode: serviceSource.countryCode || '',
                stateCode: serviceSource.stateCode || '',
                cityCode: serviceSource.cityCode || '',
                postcodeCode: serviceSource.postcodeCode || '',
                IsSync: serviceSource.IsSync ?? serviceSource.isSync,
            };
            console.log('🔍 [ContactDetailPage] newContactInfoData.altLocalAddress 생성 완료:', newContactInfoData.altLocalAddress);
        } else {
            newContactInfoData.altLocalAddress = {
                addressLine1: '',
                addressLine2: '',
                addressLine3: '',
                country: '',
                state: '',
                city: '',
                postcode: '',
                post_code: '',
                countryCode: '',
                stateCode: '',
                cityCode: '',
                postcodeCode: '',
            };
            console.log('🔍 [ContactDetailPage] MY Service 없음 — altLocalAddress 빈 값 (Residential 미복사)');
        }
    }
    
    if (!newContactInfoData.altLocalAddress) {
        // MY가 아닌 경우 기존 방식
        newContactInfoData.altLocalAddress = {
            postalCode: contact.ALT_POSTAL_CODE || '',
            blockHouseNo: contact.ALT_BLOCK_NO || '',
            streetName: contact.ALT_STREET_NAME || '',
            buildingEstateName: contact.ALT_BUILDING_NAME || '',
            level: contact.ALT_ADDR_LEVEL || '',
            unit: contact.ALT_ADDR_UNIT || '',
        };
        newContactInfoData.altForeignAddress = {
            addressLine1: contact.ALT_FOREIGN_ADDRESS_LINE_1 || '',
            addressLine2: contact.ALT_FOREIGN_ADDRESS_LINE_2 || '',
        };
    }
    
    try {
        if (
            useBranchHydrate ||
            !contactInfoData.value ||
            JSON.stringify(contactInfoData.value) !== JSON.stringify(newContactInfoData)
        ) {
            contactInfoData.value = newContactInfoData as any;
        }
    } catch (contactInfoAssignErr) {
        console.warn('⚠️ [ContactDetailPage] contactInfoData 할당 실패 — 직접 할당', contactInfoAssignErr);
        contactInfoData.value = newContactInfoData as any;
    }
    } catch (hydrateErr) {
        console.error('[ContactDetailPage] hydrateDetailFromContact failed', hydrateErr);
        if (useBranchHydrate) {
            addressData.value = createBranchAddressDataShell(contact) as any;
            contactInfoData.value = {
                countryCode: contact.COUNTRY_CD || '',
                areaCode: contact.AREA_CODE || '',
                mobileNos: contact.MOBILE_NO ? [contact.MOBILE_NO] : [''],
                emails: [''],
                USE_BRANCH_ADDRESSES: true,
                serviceAddressList: branchAddressListFromContact(contact, 'service_address'),
                businessAddressList: branchAddressListFromContact(contact, 'business_address'),
                residential_address: Array.isArray((contact as any).residential_address)
                    ? (contact as any).residential_address
                    : [],
            } as any;
        }
    } finally {
        if (contact && !addressData.value) {
            console.warn('[ContactDetailPage] hydrate: addressData missing — applying branch shell');
            addressData.value = createBranchAddressDataShell(contact) as any;
        }
        console.log('[ContactDetailPage] hydrate: end', {
            contactCd: contact?.CONTACT_CD,
            hasAddressData: !!addressData.value,
            hasContactInfoData: !!contactInfoData.value,
            useBranch: !!(contact as any)?.USE_BRANCH_ADDRESSES,
        });
        nextTick(() => {
            isSyncingContactData.value = false;
        });
    }
}

// addressData 변경 시 contactStore.currentContact 업데이트
watch(addressData, (newAddressData) => {
    if (!newAddressData || !contactStore.currentContact) return;
    // MY Corporate branch: residential_address[] 가 소스 — addressData↔store 동기화 시 루프
    if ((contactStore.currentContact as any)?.USE_BRANCH_ADDRESSES) return;

    // 실제로 변경된 경우에만 업데이트 (무한 루프 방지)
    const currentAddress = {
        ADDRESS_TYPE_CD: contactStore.currentContact.ADDRESS_TYPE_CD || 'B07001',
        POSTAL_CODE: contactStore.currentContact.POSTAL_CODE || '',
        BLOCK_NO: contactStore.currentContact.BLOCK_NO || '',
        STREET_NAME: contactStore.currentContact.STREET_NAME || '',
        BUILDING_NAME: contactStore.currentContact.BUILDING_NAME || '',
        ADDR_LEVEL: contactStore.currentContact.ADDR_LEVEL || '',
        ADDR_UNIT: contactStore.currentContact.ADDR_UNIT || '',
        FOREIGN_ADDRESS_LINE_1: contactStore.currentContact.FOREIGN_ADDRESS_LINE_1 || '',
        FOREIGN_ADDRESS_LINE_2: contactStore.currentContact.FOREIGN_ADDRESS_LINE_2 || '',
    };
    
    const newAddress = {
        ADDRESS_TYPE_CD: newAddressData.addressType,
        POSTAL_CODE: newAddressData.localAddress?.postalCode || '',
        BLOCK_NO: newAddressData.localAddress?.blockHouseNo || '',
        STREET_NAME: newAddressData.localAddress?.streetName || '',
        BUILDING_NAME: newAddressData.localAddress?.buildingEstateName || '',
        ADDR_LEVEL: newAddressData.localAddress?.level || '',
        ADDR_UNIT: newAddressData.localAddress?.unit || '',
        FOREIGN_ADDRESS_LINE_1: newAddressData.foreignAddress?.addressLine1 || '',
        FOREIGN_ADDRESS_LINE_2: newAddressData.foreignAddress?.addressLine2 || '',
    };
    
    // 실제로 변경된 경우에만 업데이트
    if (JSON.stringify(currentAddress) !== JSON.stringify(newAddress)) {
        contactStore.currentContact = {
            ...contactStore.currentContact,
            ...newAddress
        };
    }
}, { deep: true });

// contactInfoData 변경 시 contactStore.currentContact 업데이트
watch(contactInfoData, (newContactInfoData) => {
    if (!newContactInfoData || !contactStore.currentContact) return;
    if (isSyncingContactData.value) return;
    isSyncingContactData.value = true;

    const updatedFields: Record<string, any> = {};
    const compareAndSet = (key: string, newValue: any) => {
        const currentValue = (contactStore.currentContact as any)[key];
        const hasDifference = Array.isArray(newValue) && Array.isArray(currentValue)
            ? JSON.stringify(currentValue) !== JSON.stringify(newValue)
            : currentValue !== newValue;
        if (hasDifference) {
            updatedFields[key] = newValue;
        }
    };

    compareAndSet('COUNTRY_CD', newContactInfoData.countryCode || '');
    compareAndSet('AREA_CODE', newContactInfoData.areaCode || '');
    compareAndSet('MOBILE_NO', newContactInfoData.mobileNos?.[0] || '');
    compareAndSet('CONTACT_EMAIL', newContactInfoData.emails?.[0] || '');
    compareAndSet('CONTACT_EMAILS', newContactInfoData.emails || []);
    compareAndSet('EMAIL_MAIN_YN', newContactInfoData.emailMainYn || []);
    compareAndSet('EMAIL_SYNC_YN', newContactInfoData.emailSyncYn || []);
    compareAndSet('NOTE', newContactInfoData.note || '');
    // Alternative Address 필드 추가 (ALT_ADDRESS_TYPE_CD는 저장하지 않음)
    compareAndSet('ALT_POSTAL_CODE', (newContactInfoData as any).ALT_POSTAL_CODE || '');
    compareAndSet('ALT_BLOCK_NO', (newContactInfoData as any).ALT_BLOCK_NO || '');
    compareAndSet('ALT_STREET_NAME', (newContactInfoData as any).ALT_STREET_NAME || '');
    compareAndSet('ALT_BUILDING_NAME', (newContactInfoData as any).ALT_BUILDING_NAME || '');
    compareAndSet('ALT_ADDR_LEVEL', (newContactInfoData as any).ALT_ADDR_LEVEL || '');
    compareAndSet('ALT_ADDR_UNIT', (newContactInfoData as any).ALT_ADDR_UNIT || '');
    compareAndSet('ALT_FOREIGN_ADDRESS_LINE_1', (newContactInfoData as any).ALT_FOREIGN_ADDRESS_LINE_1 || '');
    compareAndSet('ALT_FOREIGN_ADDRESS_LINE_2', (newContactInfoData as any).ALT_FOREIGN_ADDRESS_LINE_2 || '');

    if (Object.keys(updatedFields).length > 0) {
        contactStore.currentContact = {
            ...contactStore.currentContact,
            ...updatedFields
        };
    }

    nextTick(() => {
        isSyncingContactData.value = false;
    });
}, { deep: true });

// register_type이 A06001인지 확인
const isBizFileRegistrant = computed(() => {
    const registerType = contactStore.currentContact?.CONTACT_REGISTER_TYPE || 
                        (contactStore.currentContact as any)?.contactRegisterType || '';
    return String(registerType).trim().toUpperCase() === 'A06001';
});

const entityCdForDetail = computed(() => String(props.entityCd || '').trim());

/** 주소 Delete 버튼 — 항상 표시 (편집 가능 시) */
const hideAddressDeleteOnEntityDetail = computed(() => false);

const registerAddressDeleteContextForDetail = computed(() => {
    const ctx = entityRegisterAddressContext.value;
    if (!ctx || !useEntityRegisterAddressMode.value) return null;
    const contactCd = String(contactId.value || '').trim();
    if (!contactCd) return null;
    return {
        entityCd: ctx.entityCd,
        contactCd,
        positionCd: ctx.positionCd,
        positionName: props.rememberedPosition?.positionName || '',
        categoryCd: entityRegisterCategoryCdOnDetail.value,
    };
});

/** 세션 클라이언트 관할이 MY인지 (USE_BRANCH_ADDRESSES 폴백 없음) */
const isMyClientSession = computed(() => {
    const j = String(sessionStore.user?.customer?.jurisdiction || '').trim().toUpperCase();
    return j === 'MY' || j === 'MALAYSIA';
});

/**
 * MY Contact List → Contact Detail 전용 (entityCd 없음, Contact 메뉴/라우트).
 * 아코디언·Service/Business 숨김·flat address UI 범위.
 */
const isContactListMyDetail = computed(() => {
    if (!isMyClientSession.value || entityCdForDetail.value) return false;
    if (props.contactListMenuMode) return true;
    return String(route.name || '') === 'ContactDetail';
});

/** Contact List(Registrable·메뉴) — Service/Business 블록 숨김. Entity Detail(entityCd)은 유지 */
const hideServiceBusinessOnContactMenu = computed(() => isContactListMyDetail.value);

const entityRegisterAddressContext = computed(() => {
    const cid = String(contactId.value || props.contactId || '').trim();
    if (!cid) return null;
    return resolveEntityRegisterDetailAddressContext({
        entityCd: entityCdForDetail.value,
        contactCd: cid,
        registerRolePositionCd: props.registerRolePositionCd,
        entityOfficerOverlay: props.entityOfficerOverlay,
        entityBeneficialOwnerOverlay: props.entityBeneficialOwnerOverlay,
    });
});

const useEntityRegisterAddressMode = computed(() => !!entityRegisterAddressContext.value);

/** MY Entity Detail — Director/Secretary 탭에서 Business Address 블록 숨김 */
const hideBusinessAddressOnEntityOfficerDetail = computed(() => {
    if (!isMyClientSession.value || !entityCdForDetail.value) return false;
    const pos = entityRegisterAddressContext.value?.positionCd;
    return (
        pos === MY_REGISTER_POSITION_CD.DIRECTOR
        || pos === MY_REGISTER_POSITION_CD.SECRETARY
    );
});

const registerOfficerAddressModeOnDetail = computed(
    () => useEntityRegisterAddressMode.value && !!props.entityOfficerOverlay,
);

const showRegisterBusinessAddressOnDetail = computed(
    () => entityRegisterAddressContext.value?.positionCd === MY_REGISTER_POSITION_CD.BENEFICIAL_OWNER,
);

const entityRegisterCategoryCdOnDetail = computed(() =>
    String(
        (contactStore.currentContact as any)?.CATEGORY_CD
        || (contactStore.currentContact as any)?.categoryCd
        || 'B09002',
    ).trim(),
);

const registerDetailHydratedState = ref<MyContactDetailFormState | null>(null);

/**
 * Corporate Registered/Service/Business 주소 폼 readonly.
 * BizFile(A06001) + Entity 컨텍스트(entityCd)일 때만 Registered 등 branch 폼 제한.
 * Contact List(/Contact, entityCd 없음)에서는 A06001이어도 Registered + Add/Edit 표시.
 */
const branchAddressFormReadonly = computed(() => {
    if (isMyJurisdictionDetail.value) return false;
    return isBizFileRegistrant.value && !!entityCdForDetail.value;
});

/** MY Detail: BizFile(A06001)이어도 이름 외 섹션 편집 허용 */
const contactDetailFormReadonly = computed(() => {
    if (isMyJurisdictionDetail.value) return false;
    return isBizFileRegistrant.value;
});

/** MY Corporate branch Registered — ContactAddressForm 편집 draft */
const branchResidentialDraft = ref<any[]>([]);

function onBranchResidentialListUpdate(list: any[]) {
    branchResidentialDraft.value = Array.isArray(list) ? list.map((r) => ({ ...r })) : [];
    if (!contactInfoData.value) return;
    isSyncingContactData.value = true;
    try {
        (contactInfoData.value as any).residential_address = [...branchResidentialDraft.value];
    } finally {
        nextTick(() => {
            isSyncingContactData.value = false;
        });
    }
}

/** Contact List MY Individual — L_CONTACT_ADDRESS 다건 UI (Corporate는 branch 모드) */
const contactListFlatAddressMode = computed(
    () =>
        hideServiceBusinessOnContactMenu.value && !useBranchAddressesOnDetail.value,
);

/** MY Corporate branch: store 또는 hydrate된 contactInfoData → Registered 목록 */
const branchResidentialAddressList = computed(() => {
    const c = contactStore.currentContact as any;
    const info = contactInfoData.value as any;
    const useBranch = !!(c?.USE_BRANCH_ADDRESSES ?? info?.USE_BRANCH_ADDRESSES);
    const useFlatMulti = contactListFlatAddressMode.value;
    if (!useBranch && !useFlatMulti) return [];
    const ra = info?.residential_address ?? c?.residential_address;
    if (Array.isArray(ra)) return ra.map((x: any) => ({ ...x }));
    if (ra && typeof ra === 'object') return [{ ...ra }];
    return [];
});

const useBranchAddressesOnDetail = computed(() => {
    if (registerDetailHydratedState.value) {
        return registerDetailHydratedState.value.useBranchAddresses;
    }
    const c = contactStore.currentContact as any;
    const info = contactInfoData.value as any;
    return !!(c?.USE_BRANCH_ADDRESSES ?? info?.USE_BRANCH_ADDRESSES);
});

const branchServiceAddressList = computed(() => {
    if (!useBranchAddressesOnDetail.value) return [];
    const info = contactInfoData.value as any;
    if (Array.isArray(info?.serviceAddressList)) return info.serviceAddressList;
    return [];
});

const branchBusinessAddressList = computed(() => {
    if (!useBranchAddressesOnDetail.value) return [];
    const info = contactInfoData.value as any;
    if (Array.isArray(info?.businessAddressList)) return info.businessAddressList;
    return [];
});

/** Entity Officer: Show Address = contact 전체 branch 풀 (카드 슬롯은 entity MAP 필터 유지) */
const branchAddressShowPool = computed(() => {
    const c = contactStore.currentContact as any;
    return c?.branch_address_show_pool ?? c?.BRANCH_ADDRESS_SHOW_POOL ?? null;
});

const branchResidentialShowPool = computed(() => {
    if (registerDetailHydratedState.value) {
        return registerDetailHydratedState.value.branchResidentialShowPool;
    }
    if (entityCdForDetail.value && branchAddressShowPool.value?.residential) {
        return branchAddressShowPool.value.residential;
    }
    if (contactListFlatAddressMode.value) {
        return branchResidentialAddressList.value;
    }
    return branchResidentialAddressList.value;
});

const branchServiceShowPool = computed(() => {
    if (registerDetailHydratedState.value) {
        return registerDetailHydratedState.value.branchServiceShowPool;
    }
    if (entityCdForDetail.value && branchAddressShowPool.value?.service) {
        return branchAddressShowPool.value.service;
    }
    return branchServiceAddressList.value;
});

const branchBusinessShowPool = computed(() => {
    if (registerDetailHydratedState.value) {
        return registerDetailHydratedState.value.branchBusinessShowPool;
    }
    if (entityCdForDetail.value && branchAddressShowPool.value?.business) {
        return branchAddressShowPool.value.business;
    }
    return branchBusinessAddressList.value;
});

/** Registered Address 카드 표시 (addressData 필수 — getter 매 틱 신규 객체 방지) */
const showRegisteredAddressForm = computed(() => {
    if (!contactStore.currentContact || !addressData.value) return false;
    return contactStore.isCorporate || contactStore.isIndividual;
});

/** Corporate branch인데 Registered 폼이 마운트되지 않을 때 디버그용 */
const showRegisteredAddressMissingHint = computed(
    () =>
        !!contactStore.currentContact &&
        contactStore.isCorporate &&
        useBranchAddressesOnDetail.value &&
        !showRegisteredAddressForm.value,
);

onErrorCaptured((err, _instance, info) => {
    console.error('[ContactDetailPage] child component error', err, info);
    return false;
});

/** Corporate/Individual 분기 실패 시 디버그용 */
const isDetailCategoryUnresolved = computed(
    () =>
        !!contactStore.currentContact &&
        !contactStore.isIndividual &&
        !contactStore.isCorporate,
);

// Individual Detail에서 Identification No. readonly 필드 계산
// registrant가 'From BizFile' (CONTACT_REGISTER_TYPE === 'A06001')인 경우 Identification No.는 수정 불가
// registrant가 'From Manual' (CONTACT_REGISTER_TYPE === 'A06002' 또는 기타)인 경우 Identification No.는 수정 가능
// Nationality/Citizenship은 항상 수정 가능 (드롭다운 작동)
// First Name, Middle Name, Last Name은 항상 수정 가능
const individualReadonlyFields = computed(() => {
    if (isMyJurisdictionDetail.value) {
        return ['CONTACT_NAME'];
    }
    if (!contactStore.currentContact || !contactStore.isIndividual) {
        return ['CONTACT_REG_NO'];
    }
    if (contactStore.currentContact.CONTACT_REGISTER_TYPE === 'A06001') {
        return ['CONTACT_REG_NO'];
    }
    return [];
});

const isManualRegistrant = computed(() => {
    const storeType = contactStore.currentContact?.CONTACT_REGISTER_TYPE || (contactStore.currentContact as any)?.contactRegisterType || '';
    const infoType = contactInfoData.value?.CONTACT_REGISTER_TYPE || contactInfoData.value?.contactRegisterType || '';
    const normalizedStore = String(storeType).trim().toUpperCase();
    const normalizedInfo = String(infoType).trim().toUpperCase();
    const isStoreManual = !normalizedStore || normalizedStore !== 'A06001';
    const isInfoManual = !normalizedInfo || normalizedInfo !== 'A06001';
    return isStoreManual || isInfoManual;
});

const shouldLockPrimaryEmail = computed(() => {
    if (isMyJurisdictionDetail.value) return false;
    return !isManualRegistrant.value;
});

// IS_POSTAL_CD_SYNC 값 확인
const isPostalCdSyncEnabled = computed(() => {
    const user = sessionStore.user;
    if (!user) return false;
    
    const syncProps = (user as any)?.syncProps;
    if (!syncProps) return false;
    
    // syncProps가 객체인 경우
    if (typeof syncProps === 'object' && !Array.isArray(syncProps)) {
        return syncProps.IS_POSTAL_CD_SYNC === 'Y';
    }
    
    return false;
});

// 선택 핸들러
const handleSelect = () => {
    if (contactStore.currentContact) {
        // select 이벤트 emit (ContactListPage에서 처리)
        emit('select', contactStore.currentContact);
        // onSelect prop이 있으면 직접 호출
        if (props.onSelect) {
            props.onSelect(contactStore.currentContact);
        }
    }
};

// 뒤로가기 핸들러
const handleBack = () => {
    if (props.isModal) {
        emit('back');
    } else {
        router.push('/Contact');
    }
};

// contactId 변경 시 데이터 로드
// immediate:true로 초기 마운트도 커버되므로 onMounted 중복 호출 제거
watch(
    () => [contactId.value, String(props.entityCd || '').trim()] as const,
    ([newId]) => {
        myResidentialAddressEditing.value = false;
        if (newId) {
            loadContact();
        }
    },
    { immediate: true },
);
</script>

<template>
    <v-container fluid class="page-wrapper">
        <v-row>
            <v-col cols="12">
                <!-- 브레드크럼 -->
                <div class="mb-2">
                    <div class="page-title">{{ t('contact.Contact') || 'Contact' }}</div>
                    <div class="breadcrumb-line">
                        <span class="breadcrumb-text">{{ t('contact.Home') || 'Home' }} /</span>
                        <span class="breadcrumb-text">{{ t('contact.Contact') || 'Contact' }} /</span>
                        <span class="breadcrumb-link breadcrumb-current">{{ contactStore.isIndividual 
                            ? (t('contact.Individual Detail') || 'Individual Detail')
                            : (t('contact.Corporate Detail') || 'Corporate Detail')
                        }}</span>
                    </div>
                </div>
                <!-- 헤더 -->
                <v-card class="mb-4 contact-header-card">
                    <v-card-title class="d-flex justify-space-between align-center">
                        <div>
                            <h2 class="contact-title">
                                {{ contactStore.isIndividual 
                                    ? (t('contact.Individual Detail') || 'Individual Detail')
                                    : (t('contact.Corporate Detail') || 'Corporate Detail')
                                }}
                            </h2>
                            <v-chip 
                                v-if="contactStore.currentContact"
                                class="mt-2"
                                :color="contactStore.isIndividual ? 'primary' : 'secondary'"
                            >
                                {{ contactStore.isIndividual 
                                    ? (t('contact.Individual') || 'Individual')
                                    : (t('contact.Corporate') || 'Corporate')
                                }}
                            </v-chip>
                        </div>
                        <div class="d-flex gap-2">
                            <v-btn 
                                v-if="isModal && props.onSelect != null"
                                color="primary" 
                                variant="flat"
                                class="main-button"
                                @click="handleSelect"
                            >
                                <v-icon left>mdi-check</v-icon>
                                {{ t('startTask.Select') || 'Select' }}
                            </v-btn>
                            <v-btn 
                                color="primary" 
                                variant="outlined"
                                class="sub-button"
                                @click="handleBack"
                            >
                                <v-icon left>mdi-arrow-left</v-icon>
                                {{ t('contact.Back to List') || 'Back to List' }}
                            </v-btn>
                        </div>
                    </v-card-title>
                </v-card>

                    <!-- 로딩 상태 -->
                    <v-card v-if="contactStore.isLoading" :loading="true" />

                    <!-- Contact 상세 정보 -->
                    <template v-else-if="contactStore.currentContact">
                        <!-- Individual (CATEGORY_CD = 'B09002') -->
                        <div v-if="contactStore.isIndividual">
                            <!-- 1. BasicIndividualSection -->
                            <BasicIndividualSection
                                v-model="contactStore.currentContact"
                                :readonly="contactDetailFormReadonly"
                                :readonly-fields="individualReadonlyFields"
                                :detail-mode="true"
                                :hide-kyc-status="true"
                                :sync-changes="syncFieldChanges"
                                :entity-officer-overlay="entityOfficerForm"
                                @update:entity-officer-overlay="entityOfficerForm = $event"
                                :entity-beneficial-owner-overlay="entityBeneficialOwnerForm"
                                @update:entity-beneficial-owner-overlay="entityBeneficialOwnerForm = $event"
                                class="mb-4"
                            />

                            <!-- 로컬만: Address Consolidation (MY re-sync | compare) -->
                            <div
                                v-if="!useEntityRegisterAddressMode && showLocalAddressConsolidationToolbarRow && !hideServiceBusinessOnContactMenu"
                                class="d-flex justify-start align-center ga-2 flex-wrap mb-2"
                            >
                                <v-btn
                                    v-if="showMySpcAddressConsolidationReSyncToolbar"
                                    size="small"
                                    color="primary"
                                    variant="tonal"
                                    prepend-icon="mdi-sync"
                                    @click="openAddressCompareSpc('resync')"
                                >
                                    Address Consolidation
                                </v-btn>
                                <v-btn
                                    v-if="isDevCompareEnabled"
                                    size="small"
                                    color="info"
                                    variant="tonal"
                                    prepend-icon="mdi-database-search"
                                    @click="openAddressCompareSpc('compare')"
                                >
                                    Address Consolidation
                                </v-btn>
                            </div>

                            <!-- 2. ContactAddressForm 또는 NoSyncContactAddressForm -->
                            <ContactAddressForm
                                ref="contactAddressFormRef"
                                v-if="showRegisteredAddressForm && isPostalCdSyncEnabled"
                                :key="`addr-sync-${contactId}`"
                                :hide-postal-search="isBizFileRegistrant"
                                v-model="addressData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="branchAddressFormReadonly"
                                :use-branch-addresses="useBranchAddressesOnDetail"
                                :is-individual="true"
                                :contact-list-flat-address-mode="contactListFlatAddressMode"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                :hide-address-consolidation-ui="hideServiceBusinessOnContactMenu"
                                :residential-address-list="branchResidentialAddressList"
                                :residential-address-show-pool="branchResidentialShowPool"
                                :service-address-list-for-show="branchServiceShowPool"
                                :business-address-list-for-show="branchBusinessShowPool"
                                :resolve-branch-lists-for-persist="resolveBranchListsForPersist"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                :on-re-sync-address-click="() => openAddressCompareSpc('resync', 'Residential')"
                                :sync-changes="syncFieldChanges"
                                @update:residential-address-list="onBranchResidentialListUpdate"
                                @refresh="loadContact"
                                class="mb-4"
                            />
                            <NoSyncContactAddressForm
                                ref="contactAddressFormRef"
                                v-else-if="showRegisteredAddressForm"
                                :key="`addr-nosync-${contactId}`"
                                v-model="addressData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="branchAddressFormReadonly"
                                :use-branch-addresses="useBranchAddressesOnDetail"
                                :is-individual="true"
                                :contact-list-flat-address-mode="contactListFlatAddressMode"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                :hide-address-consolidation-ui="hideServiceBusinessOnContactMenu"
                                :residential-address-list="branchResidentialAddressList"
                                :residential-address-show-pool="branchResidentialShowPool"
                                :service-address-list-for-show="branchServiceShowPool"
                                :business-address-list-for-show="branchBusinessShowPool"
                                :resolve-branch-lists-for-persist="resolveBranchListsForPersist"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                :on-re-sync-address-click="() => openAddressCompareSpc('resync', 'Residential')"
                                @update:residential-address-list="onBranchResidentialListUpdate"
                                @refresh="loadContact"
                                class="mb-4"
                            />

                            <!-- 3. ContactInfoForm -->
                            <v-row
                                v-if="entityCdForDetail && props.rememberedPosition?.positionName"
                                dense
                                class="mb-2"
                            >
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :model-value="props.rememberedPosition!.positionName"
                                        :label="t('entity.Position') || 'Position'"
                                        readonly
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        class="readonly-field"
                                    >
                                        <template #append-inner>
                                            <SyncFieldIndicator :change="syncFieldChange('positionsHeld', 'positions_held')" />
                                        </template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <ContactInfoForm
                                ref="contactInfoFormRef"
                                v-if="contactInfoData"
                                :key="`info-${contactId}`"
                                v-model="contactInfoData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="contactDetailFormReadonly"
                                :my-detail-editable-except-name="isMyJurisdictionDetail"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :hide-business-address-section="hideBusinessAddressOnEntityOfficerDetail"
                                :require-email="true"
                                :lock-primary-email="shouldLockPrimaryEmail"
                                :force-unlock-primary-email="isManualRegistrant || isMyJurisdictionDetail"
                                :manual-override="isManualRegistrant || isMyJurisdictionDetail"
                                :show-sync-badge="!isManualRegistrant && !!currentEmailValue"
                                :is-individual="true"
                                :residential-address-list-for-show="branchResidentialShowPool"
                                :service-address-show-pool="branchServiceShowPool"
                                :business-address-show-pool="branchBusinessShowPool"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                :show-service-multi-hint="showServiceMultiHintOnDetail"
                                :service-multi-hint-text="SERVICE_MULTI_SPC_HINT"
                                :show-business-multi-hint="showBusinessMultiHintOnDetail && !hideBusinessAddressOnEntityOfficerDetail"
                                :business-multi-hint-text="BUSINESS_MULTI_SPC_HINT"
                                :hide-address-sections="hideServiceBusinessOnContactMenu"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                :on-re-sync-address-click="(g) => openAddressCompareSpc('resync', g)"
                                :sync-changes="syncFieldChanges"
                                @refresh="loadContact"
                            />
                            <CompanyRelationsTable
                                :contact-id="contactId"
                                :contact-name="String((contactStore.currentContact as any)?.CONTACT_NAME || (contactStore.currentContact as any)?.contactName || '')"
                                :contact-reg-no="String((contactStore.currentContact as any)?.CONTACT_REG_NO || (contactStore.currentContact as any)?.contactRegNo || '')"
                                :contact-json-data="(contactInfoData as any)?.CONTACT_JSON_DATA || (contactInfoData as any)?.contact_json_data || (contactStore.currentContact as any)?.CONTACT_JSON_DATA || (contactStore.currentContact as any)?.contact_json_data || null"
                                :profile-json-data="(contactStore.currentContact as any)?.JSON_DATA ?? (contactStore.currentContact as any)?.json_data ?? null"
                                :residential-address="(contactInfoData as any)?.residential_address"
                                class="mb-4"
                            />
                        </div>

                        <v-alert
                            v-else-if="isDetailCategoryUnresolved"
                            type="warning"
                            variant="tonal"
                            class="mb-4"
                        >
                            Unknown contact category:
                            {{ (contactStore.currentContact as any)?.CATEGORY_CD || '(empty)' }}
                        </v-alert>

                        <!-- Corporate (CATEGORY_CD = 'B09001') -->
                        <div v-else-if="contactStore.isCorporate">
                            <!-- 1. BasicCorporateSection -->
                            <BasicCorporateSection
                                v-model="contactStore.currentContact"
                                :readonly="contactDetailFormReadonly"
                                :readonly-fields="isMyJurisdictionDetail ? ['CONTACT_NAME'] : (isBizFileRegistrant ? ['CONTACT_NAME', 'CONTACT_REG_NO'] : ['CONTACT_NAME'])"
                                :detail-mode="true"
                                :entity-officer-overlay="entityOfficerForm"
                                @update:entity-officer-overlay="entityOfficerForm = $event"
                                class="mb-4"
                            />

                            <!-- 로컬만: Address Consolidation (MY re-sync | compare) -->
                            <div
                                v-if="!useEntityRegisterAddressMode && showLocalAddressConsolidationToolbarRow && !hideServiceBusinessOnContactMenu"
                                class="d-flex justify-start align-center ga-2 flex-wrap mb-2"
                            >
                                <v-btn
                                    v-if="showMySpcAddressConsolidationReSyncToolbar"
                                    size="small"
                                    color="primary"
                                    variant="tonal"
                                    prepend-icon="mdi-sync"
                                    @click="openAddressCompareSpc('resync')"
                                >
                                    Address Consolidation
                                </v-btn>
                                <v-btn
                                    v-if="isDevCompareEnabled"
                                    size="small"
                                    color="info"
                                    variant="tonal"
                                    prepend-icon="mdi-database-search"
                                    @click="openAddressCompareSpc('compare')"
                                >
                                    Address Consolidation
                                </v-btn>
                            </div>

                            <v-alert
                                v-if="showRegisteredAddressMissingHint"
                                type="warning"
                                variant="tonal"
                                class="mb-4"
                            >
                                {{ hideServiceBusinessOnContactMenu ? 'Address' : 'Registered Address' }} could not be loaded (addressData is missing). Check the browser console for
                                [ContactDetailPage] logs.
                            </v-alert>

                            <!-- 2. ContactAddressForm 또는 NoSyncContactAddressForm -->
                            <ContactAddressForm
                                ref="contactAddressFormRef"
                                v-if="showRegisteredAddressForm && isPostalCdSyncEnabled"
                                :key="`addr-sync-${contactId}`"
                                :hide-postal-search="isBizFileRegistrant"
                                v-model="addressData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="branchAddressFormReadonly"
                                :use-branch-addresses="useBranchAddressesOnDetail"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                :hide-address-consolidation-ui="hideServiceBusinessOnContactMenu"
                                :residential-address-list="branchResidentialAddressList"
                                :residential-address-show-pool="branchResidentialShowPool"
                                :service-address-list-for-show="branchServiceShowPool"
                                :business-address-list-for-show="branchBusinessShowPool"
                                :resolve-branch-lists-for-persist="resolveBranchListsForPersist"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                @update:residential-address-list="onBranchResidentialListUpdate"
                                @refresh="loadContact"
                                :sync-changes="syncFieldChanges"
                                class="mb-4"
                            />
                            <NoSyncContactAddressForm
                                ref="contactAddressFormRef"
                                v-else-if="showRegisteredAddressForm"
                                :key="`addr-nosync-${contactId}`"
                                v-model="addressData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="branchAddressFormReadonly"
                                :use-branch-addresses="useBranchAddressesOnDetail"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                :hide-address-consolidation-ui="hideServiceBusinessOnContactMenu"
                                :residential-address-list="branchResidentialAddressList"
                                :residential-address-show-pool="branchResidentialShowPool"
                                :service-address-list-for-show="branchServiceShowPool"
                                :business-address-list-for-show="branchBusinessShowPool"
                                :resolve-branch-lists-for-persist="resolveBranchListsForPersist"
                                @update:residential-address-list="onBranchResidentialListUpdate"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                @refresh="loadContact"
                                class="mb-4"
                            />

                            <!-- 3. ContactInfoForm -->
                            <ContactInfoForm
                                ref="contactInfoFormRef"
                                v-if="contactInfoData"
                                :key="`info-${contactId}`"
                                v-model="contactInfoData"
                                :contact-cd="contactId"
                                :entity-cd="entityCdForDetail || undefined"
                                :readonly="branchAddressFormReadonly"
                                :my-detail-editable-except-name="isMyJurisdictionDetail"
                                :hide-address-delete="hideAddressDeleteOnEntityDetail"
                                :register-address-delete-context="registerAddressDeleteContextForDetail"
                                :hide-business-address-section="hideBusinessAddressOnEntityOfficerDetail"
                                :residential-address-list-for-show="branchResidentialShowPool"
                                :service-address-show-pool="branchServiceShowPool"
                                :business-address-show-pool="branchBusinessShowPool"
                                :require-email="false"
                                :lock-primary-email="isMyJurisdictionDetail ? false : !isManualRegistrant"
                                :force-unlock-primary-email="isManualRegistrant || isMyJurisdictionDetail"
                                :manual-override="isManualRegistrant || isMyJurisdictionDetail"
                                :show-sync-badge="!isManualRegistrant && !!currentEmailValue"
                                :is-individual="false"
                                :show-residential-multi-hint="showResidentialMultiHintOnDetail"
                                :residential-multi-hint-text="RESIDENTIAL_MULTI_SPC_HINT"
                                :show-service-multi-hint="showServiceMultiHintOnDetail"
                                :service-multi-hint-text="SERVICE_MULTI_SPC_HINT"
                                :show-business-multi-hint="showBusinessMultiHintOnDetail && !hideBusinessAddressOnEntityOfficerDetail"
                                :business-multi-hint-text="BUSINESS_MULTI_SPC_HINT"
                                :hide-address-sections="hideServiceBusinessOnContactMenu"
                                :simple-address-label="hideServiceBusinessOnContactMenu"
                                @refresh="loadContact"
                                :sync-changes="syncFieldChanges"
                                class="mb-4"
                            />

                            <!-- 4. CorporateLegalInfoSection -->
                            <CorporateLegalInfoSection
                                v-model="contactStore.currentContact"
                                :readonly="contactDetailFormReadonly"
                                class="mb-4"
                            />

                            <!-- 5. Company Relations -->
                            <CompanyRelationsTable
                                :contact-id="contactId"
                                :contact-name="String((contactStore.currentContact as any)?.CONTACT_NAME || (contactStore.currentContact as any)?.contactName || '')"
                                :contact-reg-no="String((contactStore.currentContact as any)?.CONTACT_REG_NO || (contactStore.currentContact as any)?.contactRegNo || '')"
                                :contact-json-data="(contactInfoData as any)?.CONTACT_JSON_DATA || (contactInfoData as any)?.contact_json_data || (contactStore.currentContact as any)?.CONTACT_JSON_DATA || (contactStore.currentContact as any)?.contact_json_data || null"
                                :profile-json-data="(contactStore.currentContact as any)?.JSON_DATA ?? (contactStore.currentContact as any)?.json_data ?? null"
                                :residential-address="(contactInfoData as any)?.residential_address"
                                class="mb-4"
                            />
                        </div>
                        
                        <!-- 하단 버튼 (블록 없이 배치) -->
                        <div class="footer-actions">
                            <div>
                                <v-checkbox
                                    v-if="props.rememberedPosition && hasContactChanges"
                                    v-model="registerActivityHistory"
                                    class="register-history-checkbox"
                                    :label="t('entity.Turn On Register for Activity History') || 'Turn On Register for Activity History'"
                                    hide-details
                                    density="compact"
                                />
                            </div>
                            <v-btn
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="Save"
                                class="main-button"
                                :loading="isSubmitting"
                                @click="onSaveClickGuard"
                            >
                                <v-icon left>mdi-content-save</v-icon>
                                {{ t('contact.SAVE') || 'SAVE' }}
                            </v-btn>
                        </div>
                    </template>

                    <!-- 데이터 없음 -->
                    <v-card v-else>
                        <v-card-text class="text-center py-8">
                            <v-icon size="64" color="grey">mdi-alert-circle-outline</v-icon>
                            <p class="text-h6 mt-4">{{ t('contact.Contact not found') || 'Contact not found' }}</p>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- 삭제 확인 다이얼로그 -->
            <v-dialog v-model="deleteDialog" max-width="400">
                <v-card rounded="md">
                    <v-card-title>{{ t('contact.Delete Contact') || 'Delete Contact' }}</v-card-title>
                    <v-card-text>
                        {{ t('contact.Are you sure you want to delete this contact?') || 'Are you sure you want to delete this contact?' }}
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn variant="text" @click="deleteDialog = false">
                            {{ t('contact.Cancel') || 'Cancel' }}
                        </v-btn>
                        <v-btn
                            v-permission-button
                            data-menu-key="/Contact"
                            data-button-name="Delete"
                            color="error"
                            variant="outlined"
                            @click="handleDelete"
                            :loading="contactStore.isLoading"
                        >
                            {{ t('contact.Delete') || 'Delete' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- SPC 주소 비교·통합 (MY·local/dev compare 시 마운트) -->
            <AddressCompareDevDialog
                v-if="isDevCompareEnabled || isMyJurisdictionDetail"
                v-model="showAddressCompareDialog"
                :address-groups="addressCompareGroups"
                :spc-compact-ui="addressCompareSpcUi"
                :dialog-mode="addressCompareDialogMode"
                :initial-group-tab="addressCompareInitialGroupTab"
                :title="addressCompareDialogMode === 'resync' ? 'Address Consolidation' : 'Contact Address Compare (DEV)'"
                @applied="loadContact"
            />
        </v-container>
</template>

<style scoped>
/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.page-wrapper {
    padding: 24px;
}

/* 페이지 타이틀 */
.page-title {
    font-size: 21px;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.2;
}

/* 브레드크럼 스타일 */
.breadcrumb-line {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
}

.breadcrumb-text {
    font-size: 12px;
    color: rgb(var(--v-theme-on-surface));
}

.breadcrumb-link {
    font-size: 12px;
    color: #1B84FF;
    font-weight: 500;
    text-decoration: none;
}

.breadcrumb-link:hover {
    text-decoration: underline;
}

.breadcrumb-current {
    color: #1B84FF;
    font-weight: 500;
}

.contact-header-card {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

.contact-title {
    font-size: 24px;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
}

.footer-actions {
    display: flex;
    justify-content: space-between;
    padding: 16px 0;
    margin-top: 24px;
}

/* 버튼 스타일 */
.main-button {
    background-color: #1B84FF !important;
    color: white !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    text-transform: none;
}

.main-button:hover {
    background-color: #1568CC !important;
}

.sub-button {
    background-color: #D5EDFF !important;
    color: #1B84FF !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    text-transform: none;
}

.sub-button:hover {
    background-color: #C2E3FF !important;
}

/* 섹션 카드 - ContactDetailPage 전체 섹션에 적용 */
.section-card {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

/* 필수 필드 asterisk */
.required-asterisk {
    color: #F44336 !important;
    font-weight: bold;
    margin-left: 2px;
}
</style>

